import { DisplayNode, EditionMode } from './types.js';
import type { Selection } from 'd3-selection';
import { SearchDropdown } from './components/SearchDropdown.js';
import { InlineEntityEditor, EntityType, EntityData } from './components/InlineEntityEditor.js';
import { Ingredient, Transformation, TransformationInfo, Tool, ToolInfo, BUILTIN_UNIT_IDS, Quantity } from '../api/types.js';
import { createIngredient, addIngredientImage } from '../api/index.js';
import { unitCache } from '../unit-cache.js';
import { ImagePool, ImagePoolItem } from './components/ImagePool.js';
import { resolveNodeTitle } from './DisplayGraph.js';
import { authService } from '../auth/AuthService.js';
import { getNodeImageUrl, getDefaultImageUrl } from './imageUtils.js';
import { tagChipStyle } from '../tags/tagColors.js';
import { tagGraphCache } from '../tags/tagGraph.js';
import { setIcon } from '../ui/icon.js';
import { durationBadgeHtml } from './textFormatting.js';
import { renderDurationInput, type DurationInputHandle } from './components/DurationInput.js';

/** Callback to render step text HTML for a node (from transformation template). */
export type StepTextRenderer = (node: DisplayNode) => string | null;

/** Callback to render ingredients HTML for a node's children. */
export type IngredientsTextRenderer = (node: DisplayNode) => string | null;

/** Callback returning the node's computed total elaboration time in
 *  seconds, or null when the subtree has no duration set at all — see
 *  computeNodeTotalDuration()'s doc comment. Formatting (the clock badge)
 *  is built by the caller via durationBadgeHtml(). */
export type TotalDurationRenderer = (node: DisplayNode) => number | null;

/** Callback when user hovers over a node reference in rendered text. */
export type NodeHoverCallback = (nodeId: string) => void;
/** Callback when user stops hovering over a node reference in rendered text. */
export type NodeUnhoverCallback = (nodeId: string) => void;
/** Callback when user clicks a node reference in rendered text. */
export type NodeClickCallback = (nodeId: string, event?: MouseEvent) => void;

/**
 * Context for rendering the side panel.
 * Determines which view to show: recipe summary, recipe detail, or node detail.
 */
export interface VisualizerContext {
  /** The node is a main root in collapsed state — show recipe summary */
  isRecipeSummary?: boolean;
  /** The node is a main root in expanded state — show recipe detail */
  isRecipeDetail?: boolean;
  /** Recipe title (resolved from root node) */
  recipeTitle?: string;
  /** Recipe description (from root node) */
  recipeDescription?: string;
  /** Hero image URL for the recipe */
  recipeImageUrl?: string;
  /** Recipe quantity (e.g. "makes 4 portions") */
  recipeQuantity?: Quantity | null;
  /** Number of ingredients in the recipe */
  ingredientCount?: number;
  /** Whether the recipe is being edited */
  isEditing?: boolean;
  /** Whether the current node is a root node (direct child of the recipe) */
  isRootNode?: boolean;
}

/**
 * NodeVisualizer component for displaying detailed information about nodes.
 * Shows selected node info, falls back to hovered node, empty state otherwise.
 * Supports edit mode with inline editing capabilities.
 */
export class NodeVisualizer {
  private container: Selection<any, any, any, any>;
  private visualizerElement!: Selection<any, any, any, any>;
  private editionMode: () => EditionMode;
  private onFieldChange: (nodeId: string, field: string, value: unknown) => Promise<void>;
  private onNodeModified: ((nodeId: string) => void) | null = null;
  private onEntityUpdate: ((entityType: 'ingredient' | 'transformation' | 'tool', entityId: string, updatedEntity: Ingredient | Transformation | Tool) => void) | null;
  private currentNode: DisplayNode | null = null;
  private searchDropdowns: SearchDropdown[] = [];
  // Which edit tab (node/ingredient/transformation) is active. Persists
  // across re-renders of the same node (e.g. after selecting/deleting an
  // ingredient) so the popup doesn't jump back to "Node" on every change;
  // resets to "node" only when a genuinely different node is shown.
  private activeEditTab: string = 'node';
  /** The edition mode in effect the last time renderNodeContent() actually
   *  ran — used by updateNode()'s redundant-rebuild guard, see there. */
  private lastRenderedEditionMode: EditionMode | null = null;
  private scalingFactor: number = 1;
  private onStartPickNode: ((nodeId: string, paramName: string, onPicked: (pickedNodeId: string | null) => void) => () => void) | undefined;
  private pickNodeCancel: (() => void) | null = null;
  private pickInputRef: HTMLInputElement | null = null;
  private pickOriginalValue: string | undefined = undefined;
  /** Callback when user clicks "Find recipes" on an ingredient node */
  private onFindIngredientRecipes: ((nodeId: string, ingredientId: string, ingredientName: string) => void) | null = null;
  /** Callback when user clicks expand on a recipe summary */
  private onExpandNode: ((nodeId: string) => void) | null = null;
  /** Current visualizer context */
  private currentContext: VisualizerContext | null = null;
  /** Callback to render step text for a node (from transformation template). */
  private stepTextRenderer: StepTextRenderer | null = null;
  /** Callback to render ingredients text for a node's children. */
  private ingredientsTextRenderer: IngredientsTextRenderer | null = null;
  /** Callback to render a node's computed total elaboration time. */
  private totalDurationRenderer: TotalDurationRenderer | null = null;
  /** Callback when user hovers a node reference in rendered text. */
  private onNodeHover: NodeHoverCallback | null = null;
  /** Callback when user hovers a node reference - graph only, no side panel update. */
  private onNodeHoverGraphOnly: NodeHoverCallback | null = null;
  /** Callback when user stops hovering a node reference in rendered text. */
  private onNodeUnhover: NodeUnhoverCallback | null = null;
  /** Callback when user clicks a node reference in rendered text. */
  private onNodeClick: NodeClickCallback | null = null;
  /** Callback to check if a node is expanded */
  private isNodeExpanded: (nodeId: string) => boolean = () => true;
  /** Resolve a node id to its DisplayNode — see constructor doc comment. */
  private getNodeById: ((nodeId: string) => DisplayNode | undefined) | null = null;

  constructor(
    container: Selection<any, any, any, any>,
    editionMode: () => EditionMode,
    onFieldChange: (nodeId: string, field: string, value: unknown) => Promise<void>,
    onEntityUpdate?: (entityType: 'ingredient' | 'transformation' | 'tool', entityId: string, updatedEntity: Ingredient | Transformation | Tool) => void,
    onStartPickNode?: (nodeId: string, paramName: string, onPicked: (pickedNodeId: string | null) => void) => () => void,
    onFindIngredientRecipes?: (nodeId: string, ingredientId: string, ingredientName: string) => void,
    onExpandNode?: (nodeId: string) => void,
    isNodeExpanded?: (nodeId: string) => boolean,
    /** Resolve a node id to its DisplayNode — used by the Utensilis tab to
     *  tell whether a tool usage is implicitly the first instance in its
     *  lineage (none of this node's children carry the same tool). */
    getNodeById?: (nodeId: string) => DisplayNode | undefined,
  ) {
    this.container = container;
    this.editionMode = editionMode;
    this.onFieldChange = onFieldChange;
    this.onEntityUpdate = onEntityUpdate ?? null;
    this.onStartPickNode = onStartPickNode;
    this.onFindIngredientRecipes = onFindIngredientRecipes ?? null;
    this.onExpandNode = onExpandNode ?? null;
    this.isNodeExpanded = isNodeExpanded ?? (() => true);
    this.getNodeById = getNodeById ?? null;
    this.createVisualizerElement();
  }

  /**
   * Creates the visualizer HTML element within the container.
   * Fills 100% of whatever container it's given — the container itself
   * (the persistent side panel's element, or the NodeEditPopup's body)
   * is what actually constrains the width in each context, so this
   * doesn't hardcode a fixed width of its own (that used to fight with
   * the popup's width, making the popup's floating close button land
   * outside the actually-visible content on wide screens).
   */
  private createVisualizerElement(): void {
    this.visualizerElement = this.container.append('div')
      .attr('id', 'node-visualizer')
      .attr('class', 'w-full min-w-0 p-4');

    this.renderEmptyState();
  }

  /**
   * Updates the visualizer with new node information.
   * Optionally accepts a VisualizerContext to render recipe summary or detail views.
   *
   * @param node - The node to display, or null to clear
   * @param context - Optional context for rendering recipe summary/detail views
   */
  /**
   * Set a callback that renders step text HTML for a node.
   * The callback receives the DisplayNode and returns HTML string, or null if no step.
   */
  public setStepTextRenderer(renderer: StepTextRenderer | null): void {
    this.stepTextRenderer = renderer;
  }

  /**
   * Set a callback that renders ingredients HTML for a node's children.
   * The callback receives the DisplayNode and returns HTML string, or null if no ingredients.
   */
  public setIngredientsTextRenderer(renderer: IngredientsTextRenderer | null): void {
    this.ingredientsTextRenderer = renderer;
  }

  /**
   * Set a callback that renders a node's computed total elaboration time.
   */
  public setTotalDurationRenderer(renderer: TotalDurationRenderer | null): void {
    this.totalDurationRenderer = renderer;
  }

  /**
   * Set a callback that is called on every field input (real-time),
   * allowing the host to track changes without a full re-render.
   */
  public setOnNodeModified(callback: ((nodeId: string) => void) | null): void {
    this.onNodeModified = callback;
  }

  /**
   * Set the display scaling factor for quantities.
   * All displayed amounts are multiplied by this factor.
   */
  public setScalingFactor(factor: number): void {
    this.scalingFactor = factor;
  }

  /**
   * Set a callback for when the user hovers a node reference in rendered text.
   */
  public setOnNodeHover(callback: NodeHoverCallback | null): void {
    this.onNodeHover = callback;
  }

  /**
   * Set a callback for when the user hovers a node reference in rendered text.
   * This version only highlights the node in the graph, without changing the side panel.
   */
  public setOnNodeHoverGraphOnly(callback: NodeHoverCallback | null): void {
    this.onNodeHoverGraphOnly = callback;
  }

  /**
   * Set a callback for when the user stops hovering a node reference in rendered text.
   */
  public setOnNodeUnhover(callback: NodeUnhoverCallback | null): void {
    this.onNodeUnhover = callback;
  }

  /**
   * Set a callback for when the user clicks a node reference in rendered text.
   */
  public setOnNodeClick(callback: NodeClickCallback | null): void {
    this.onNodeClick = callback;
  }

  public updateNode(node: DisplayNode | null, context?: VisualizerContext): void {
    // Skip a redundant, destructive full rebuild of the same node's panel
    // in the same edition mode: whatever just mutated `node.data` (the
    // single source of truth) already reflects the change in place, and
    // if a UI refresh was genuinely needed the mutator already called
    // renderNodeContent() itself right after. Scoped to plain edit-mode
    // node editing (never the recipe summary/detail views, whose content
    // *does* depend on context fields that can legitimately change for
    // the same node) — without this, an unrelated store notification
    // (e.g. a blur handler committing a text field, itself triggered by
    // the mousedown that starts a click on some *other* control, such as
    // the image pool's "+" button) tears the panel down and rebuilds it
    // between that click's mousedown and click events, removing the
    // click's target from the DOM first and silently swallowing it.
    if (
      node &&
      this.currentNode === node &&
      this.editionMode() === EditionMode.EDIT &&
      this.lastRenderedEditionMode === EditionMode.EDIT &&
      !context?.isRecipeSummary && !context?.isRecipeDetail &&
      !this.currentContext?.isRecipeSummary && !this.currentContext?.isRecipeDetail
    ) {
      this.currentContext = context ?? null;
      return;
    }

    this.cleanupSearchDropdowns();
    if (node && this.currentNode?.id !== node.id) {
      this.activeEditTab = 'node';
    }
    this.currentNode = node;
    this.currentContext = context ?? null;

    if (!node) {
      this.renderEmptyState();
      return;
    }

    // Check for recipe summary/detail context
    if (context?.isRecipeSummary) {
      this.renderRecipeSummary(node, context);
      return;
    }

    if (context?.isRecipeDetail) {
      this.renderRecipeDetail(node, context);
      return;
    }

    // Default: render node detail (existing behavior)
    this.lastRenderedEditionMode = this.editionMode();
    this.renderNodeContent(node);
  }

  /**
   * Clears the visualizer content
   */
  public clear(): void {
    this.updateNode(null);
  }

  /**
   * Shows a small read-only card (image + title) per node — the side
   * panel's content while 2+ nodes are multi-selected, replacing the
   * normal single-node view/edit content (none of that applies to more
   * than one node at a time).
   */
  public showMultiSelection(nodes: DisplayNode[]): void {
    this.cleanupSearchDropdowns();
    this.currentNode = null;
    this.currentContext = null;

    const container = this.visualizerElement.html('').append('div').attr('class', 'space-y-2');

    container.append('div')
      .attr('class', 'text-sm font-medium text-gray-700 mb-1')
      .text(`${nodes.length} nodes seleccionats`);

    const grid = container.append('div').attr('class', 'grid grid-cols-2 gap-2');

    for (const node of nodes) {
      const imageUrl = this.resolveNodeImageUrl(node.data);
      const title = resolveNodeTitle(node.data);

      const card = grid.append('div')
        .attr('class', 'flex flex-col items-center gap-1 p-2 border border-gray-200 rounded-md text-center');

      if (imageUrl) {
        card.append('img')
          .attr('src', imageUrl)
          .attr('alt', '')
          .attr('class', 'w-12 h-12 rounded-full object-cover')
          .on('error', function () { (this as HTMLImageElement).style.display = 'none'; });
      }

      card.append('div')
        .attr('class', 'text-xs text-gray-700 break-words')
        .text(title);
    }
  }

  /**
   * Commit a field change on the next tick rather than synchronously —
   * used specifically by blur-triggered commits (name/description/notes/
   * quantity/parameters). onFieldChange ultimately fires a store
   * notification that fully rebuilds this panel; if that happened
   * synchronously inside a blur handler, it would remove whatever other
   * control the user is *in the middle of clicking* from the DOM between
   * that click's mousedown and click events — mousedown shifts focus,
   * firing this blur, before click itself fires — so the click event
   * never arrives (e.g. clicking the image pool's "+" button right after
   * typing a name used to silently do nothing). Deferring by a tick lets
   * that in-flight click complete first; the commit still lands moments
   * later, imperceptibly.
   */
  private commitFieldChangeDeferred(nodeId: string, field: string, value: unknown): void {
    setTimeout(() => { this.onFieldChange(nodeId, field, value); }, 0);
  }

  private renderEmptyState(): void {
    this.visualizerElement.html(`
      <div class="text-center text-[#a0a0a0] py-8 font-mono">
        <div class="text-lg font-bold text-[var(--accent)] mb-2">INFO DEL NODE</div>
        <div class="text-sm text-[#666]">Clica un node per mostrar</div>
      </div>
    `);
  }

  /**
   * Renders content for a specific node
   */
  private renderNodeContent(node: DisplayNode): void {
    const mode = this.editionMode();
    if (mode === EditionMode.EDIT) {
      this.renderEditableContent(node);
    } else {
      this.renderReadOnlyContent(node);
    }
  }

  // ============================================================================
  // READ-ONLY MODE
  // ============================================================================

  private renderReadOnlyContent(node: DisplayNode): void {
    const data = node.data;
    const imageUrl = this.resolveNodeImageUrl(data);
    const title = resolveNodeTitle(data);

    let html = '';

    // Image at top — circular, matching the circular treatment used
    // everywhere else a node/ingredient/transformation image is shown.
    if (imageUrl) {
      html += `<div class="w-48 h-48 mx-auto mb-2 rounded-full overflow-hidden bg-[#08080c]"><img src="${this.escapeHtml(imageUrl)}" alt="" class="w-full h-full object-cover" onerror="this.parentElement.style.display='none'" /></div>`;
    }

    // Title with orange accent
    html += `<div class="text-lg font-bold text-[var(--accent)] font-mono mt-3">&gt; ${this.escapeHtml(title)}</div>`;

    // Quantity (if available)
    if (data.ingredientInfo?.quantity) {
      const q = data.ingredientInfo.quantity;
      const scaledAmount = q.amount * this.scalingFactor;
      const unitStr = unitCache.getDisplayName(q.unit, scaledAmount);
      html += `<div class="text-sm text-[#a0a0a0] font-mono">&gt; ${this.formatScaledNumber(scaledAmount)} ${this.escapeHtml(unitStr)}</div>`;
    }

    // Computed total elaboration time for this node and everything under
    // it — never stored, see computeNodeTotalDuration()'s doc comment.
    if (this.totalDurationRenderer) {
      const totalDurationSeconds = this.totalDurationRenderer(node);
      const badge = durationBadgeHtml(totalDurationSeconds);
      if (badge) {
        html += `<div class="flex">${badge}</div>`;
      }
    }

    // Tags (the node's own ingredient's tags, if any) — explicit ones
    // render synchronously here; implied ones (via the tag-implication
    // graph) are patched in dimmed once the graph cache resolves, below.
    const tags = data.ingredientInfo?.ingredient?.tags;
    if (tags && tags.length > 0) {
      html += '<div class="node-tags-list flex flex-wrap gap-1 mt-2">';
      for (const tag of tags) {
        html += `<span class="text-xs px-2 py-0.5 rounded-full border font-mono" style="${tagChipStyle(tag.color)}">${this.escapeHtml(tag.name)}</span>`;
      }
      html += '</div>';
    }

    // Separator and content below
    // Folded-node ingredients removed — now shown in the dedicated Ingredients panel.
    // Expanded nodes: show step / preparation text
    if (this.isNodeExpanded(node.id) && this.stepTextRenderer) {
      const rendered = this.stepTextRenderer(node);
      if (rendered) {
        html += `<hr class="border-[var(--accent-alpha-20)]" />`;
        html += `<div class="text-sm text-[#e0e0e0] font-mono leading-relaxed mt-3">${rendered}</div>`;
      }
    }

    // Tool chips (e.g. "Olla", "Batedora de mà") — after the preparation
    // text, distinct styling from tags since these are a different
    // concept (equipment, not labels). Skipped entirely for a bare tool
    // node (no ingredient/transformation of its own) — its own title
    // already names the tool, so a chip repeating it would be redundant.
    const toolInfos = data.toolInfos;
    const hasOwnIdentity = !!data.ingredientInfo?.ingredient || !!data.transformationInfos?.some(ti => ti.transformation);
    if (toolInfos && toolInfos.length > 0 && hasOwnIdentity) {
      html += '<div class="flex flex-wrap gap-1 mt-2">';
      for (const toolInfo of toolInfos) {
        const freshBadge = toolInfo.fresh ? ' ⟲' : '';
        const imgUrl = getDefaultImageUrl(toolInfo.tool.images);
        const imgHtml = imgUrl
          ? `<img src="${this.escapeHtml(imgUrl)}" alt="" class="w-4 h-4 rounded-full object-cover flex-shrink-0" onerror="this.style.display='none'" />`
          : '';
        html += `<span class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-[var(--accent-alpha-40)] text-[var(--accent)] font-mono" title="${toolInfo.fresh ? 'Instància nova, no continua un ús anterior' : ''}">${imgHtml}${this.escapeHtml(toolInfo.tool.name)}${freshBadge}</span>`;
      }
      html += '</div>';
    }

    // Comment lines: node description, ingredient notes, transformation notes
    const commentLines: string[] = [];
    if (data.description) commentLines.push(data.description);
    if (data.ingredientInfo?.description) commentLines.push(data.ingredientInfo.description);
    for (const ti of data.transformationInfos ?? []) {
      if (ti.description) commentLines.push(ti.description);
    }
    for (const toolInfo of toolInfos ?? []) {
      if (toolInfo.description) commentLines.push(toolInfo.description);
    }
    for (const line of commentLines) {
      html += `<div class="text-sm text-[#888] font-mono mt-1">// ${this.escapeHtml(line)}</div>`;
    }

    // Elaboration buttons (add/modify/remove) live in the EditToolbar now
    // — see GraphVisualizer.canElaborateSelectedNode().

    this.visualizerElement.html(`<div class="font-mono px-1">${html}</div>`);

    if (tags && tags.length > 0) {
      tagGraphCache.ensureLoaded().then(() => {
        const tagsListEl = this.visualizerElement.select('.node-tags-list');
        if (tagsListEl.empty()) return; // re-rendered (selection/step change) before this resolved
        for (const implied of tagGraphCache.getImpliedTags(tags)) {
          const chip = document.createElement('span');
          chip.className = 'text-xs px-2 py-0.5 rounded-full border font-mono';
          chip.style.cssText = `${tagChipStyle(implied.color)}opacity: 0.55;`;
          chip.title = 'Implícita — no assignada directament';
          chip.textContent = implied.name;
          (tagsListEl.node() as HTMLElement).appendChild(chip);
        }
      }).catch((error) => {
        console.error('Failed to load tag implications:', error);
      });
    }

    // Wire hover events on node references in rendered text
    if (this.onNodeHoverGraphOnly || this.onNodeHover || this.onNodeUnhover || this.onNodeClick) {
      this.visualizerElement.selectAll('[data-node-id]')
        .on('mouseenter', (event: MouseEvent) => {
          const el = event.currentTarget as Element;
          const nodeId = el.getAttribute('data-node-id');
          // Use graph-only hover if available, otherwise fall back to regular hover
          if (nodeId && this.onNodeHoverGraphOnly) {
            this.onNodeHoverGraphOnly(nodeId);
          } else if (nodeId && this.onNodeHover) {
            this.onNodeHover(nodeId);
          }
        })
        .on('mouseleave', (event: MouseEvent) => {
          const el = event.currentTarget as Element;
          const nodeId = el.getAttribute('data-node-id');
          if (nodeId && this.onNodeUnhover) this.onNodeUnhover(nodeId);
        })
        .on('click', (event: MouseEvent) => {
          const el = event.currentTarget as Element;
          const nodeId = el.getAttribute('data-node-id');
          if (nodeId && this.onNodeClick) this.onNodeClick(nodeId, event);
        });
    }
  }

  // ============================================================================
  // RECIPE SUMMARY VIEW (collapsed main root node)
  // ============================================================================

  /**
   * Render the recipe summary view — shown when a collapsed main root is selected.
   * Displays: hero image, title, description, quantity, ingredient count, expand button.
   */
  private renderRecipeSummary(node: DisplayNode, context: VisualizerContext): void {
    const title = context.recipeTitle ?? resolveNodeTitle(node.data);
    const description = context.recipeDescription ?? node.data.description;
    const imageUrl = context.recipeImageUrl ?? this.resolveNodeImageUrl(node.data);
    const quantity = context.recipeQuantity;
    const ingredientCount = context.ingredientCount;
    const isEditing = context.isEditing ?? false;

    let html = '';

    // Hero image
    if (imageUrl) {
      html += `<div class="mb-4 flex justify-center"><div class="w-full h-36 rounded-lg overflow-hidden border border-[var(--accent-alpha-20)]"><img src="${this.escapeHtml(imageUrl)}" alt="" class="w-full h-full object-cover" onerror="this.parentElement.parentElement.style.display='none'" /></div></div>`;
    }

    // Title (accent color, large)
    html += `<div class="text-lg font-bold text-[var(--accent)] font-mono mb-1">${this.escapeHtml(title)}</div>`;

    // Description
    if (description) {
      html += `<div class="text-sm text-[#a0a0a0] font-mono mb-3">// ${this.escapeHtml(description)}</div>`;
    }

    // Quantity
    if (quantity && quantity.amount > 0) {
      const scaledAmount = quantity.amount * this.scalingFactor;
      const unitStr = unitCache.getDisplayName(quantity.unit, scaledAmount);
      html += `<div class="text-sm text-[#e0e0e0] font-mono mb-2">&gt; Per a ${this.formatScaledNumber(scaledAmount)} ${unitStr}</div>`;
    }

    // Ingredient count
    if (ingredientCount !== undefined) {
      html += `<div class="text-sm text-[#888] font-mono mb-4">// ${ingredientCount} ingredient${ingredientCount !== 1 ? 's' : ''}</div>`;
    }

    // Expand button (Obre)
    if (!isEditing && this.onExpandNode) {
      html += `<button class="w-full py-2 text-sm font-bold font-mono rounded-md bg-[var(--accent)] text-[#0d1117] hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all expand-recipe-btn" data-node-id="${node.id}">Obre →</button>`;
    }

    this.visualizerElement.html(`<div class="space-y-2 font-mono">${html}</div>`);

    // Wire up expand button
    if (!isEditing && this.onExpandNode) {
      const expandBtn = this.visualizerElement.select('.expand-recipe-btn');
      if (!expandBtn.empty()) {
        expandBtn.on('click', () => {
          this.onExpandNode!(node.id);
        });
      }
    }
  }

  // ============================================================================
  // RECIPE DETAIL VIEW (expanded main root node)
  // ============================================================================

  /**
   * Render the recipe detail view — shown when an expanded recipe's main root is selected.
   * Displays: hero image, title, description, quantity, ingredients list, preparation steps.
   */
  private renderRecipeDetail(node: DisplayNode, context: VisualizerContext): void {
    const title = context.recipeTitle ?? resolveNodeTitle(node.data);
    const description = context.recipeDescription ?? node.data.description;
    const imageUrl = context.recipeImageUrl ?? this.resolveNodeImageUrl(node.data);
    const quantity = context.recipeQuantity;
    const isEditing = context.isEditing ?? false;

    let html = '';

    // Hero image
    if (imageUrl) {
      html += `<div class="mb-4 flex justify-center"><div class="w-full h-36 rounded-lg overflow-hidden border border-[var(--accent-alpha-20)]"><img src="${this.escapeHtml(imageUrl)}" alt="" class="w-full h-full object-cover" onerror="this.parentElement.parentElement.style.display='none'" /></div></div>`;
    }

    // Title (accent color, large)
    html += `<div class="text-lg font-bold text-[var(--accent)] font-mono mb-1">${this.escapeHtml(title)}</div>`;

    // Description
    if (description) {
      html += `<div class="text-sm text-[#a0a0a0] font-mono mb-3">// ${this.escapeHtml(description)}</div>`;
    }

    // Quantity
    if (quantity && quantity.amount > 0) {
      const scaledAmount = quantity.amount * this.scalingFactor;
      const unitStr = unitCache.getDisplayName(quantity.unit, scaledAmount);
      html += `<div class="text-sm text-[#e0e0e0] font-mono mb-4">&gt; Per a ${this.formatScaledNumber(scaledAmount)} ${unitStr}</div>`;
    }

    // Separator before ingredients
    html += `<div class="border-t border-[var(--accent-alpha-20)] pt-3 mb-3"></div>`;

    // Ingredients section — rendered as a placeholder that external code can fill
    html += `<div id="recipe-detail-ingredients" class="mb-4">`;
    html += `  <div class="text-xs font-bold text-[var(--accent)] uppercase tracking-wider mb-2">Ingredients</div>`;
    html += `  <div class="text-sm text-[#888] font-mono">(carregant...)</div>`;
    html += `</div>`;

    // Preparation section — placeholder
    html += `<div id="recipe-detail-preparation" class="mb-4">`;
    html += `  <div class="text-xs font-bold text-[var(--accent)] uppercase tracking-wider mb-2">Preparació</div>`;
    html += `  <div class="text-sm text-[#888] font-mono">(carregant...)</div>`;
    html += `</div>`;

    this.visualizerElement.html(`<div class="space-y-2 font-mono">${html}</div>`);
  }

  /**
   * Update the ingredients list in the recipe detail view.
   * Called externally after collecting ingredients from the graph.
   */
  public updateRecipeDetailIngredients(html: string): void {
    const container = this.visualizerElement.select('#recipe-detail-ingredients');
    if (!container.empty()) {
      container.html(`
        <div class="text-xs font-bold text-[var(--accent)] uppercase tracking-wider mb-2">Ingredients</div>
        ${html}
      `);
    }
  }

  /**
   * Update the preparation steps in the recipe detail view.
   * Called externally after collecting preparation steps from the graph.
   */
  public updateRecipeDetailPreparation(html: string): void {
    const container = this.visualizerElement.select('#recipe-detail-preparation');
    if (!container.empty()) {
      container.html(`
        <div class="text-xs font-bold text-[var(--accent)] uppercase tracking-wider mb-2">Preparació</div>
        ${html}
      `);
    }
  }

  // ============================================================================
  // EDIT MODE
  // ============================================================================

  private renderEditableContent(node: DisplayNode): void {
    this.visualizerElement.html('');

    const outer = this.visualizerElement.append('div').attr('class', 'space-y-3');

    const data = node.data;

    // ── Tab bar: Node | Ingredient | Transformacions | Utensilis ──
    // Icon-only buttons (full labels would overflow the side panel at 4
    // tabs), except the active one, which also shows its full name next
    // to the icon — e.g. "▤  [⚘ Ingredient]  ↟  ⚙". Each icon's `title`
    // carries the label too, for a hover tooltip on the inactive ones.
    const tabBar = outer.append('div')
      .attr('class', 'flex gap-1 border-b border-gray-300 -mx-1 px-1');

    // `hasContent` signals (via the same accent color the active tab's
    // border/text already uses) that the node has something on that tab —
    // Node always does (name/image/description live there); the other
    // three reflect the live data, re-evaluated every activateTab() call
    // (including the one a full renderEditableContent() re-render ends
    // with), so this stays correct after any edit without extra wiring.
    const tabDefs: { key: string; label: string; icon: string; hasContent: () => boolean }[] = [
      { key: 'node', label: 'Node', icon: '▤', hasContent: () => true },
      { key: 'ingredient', label: 'Ingredient', icon: '⚘', hasContent: () => !!data.ingredientInfo?.ingredient }, // same flower as List View's "go to ingredient" button
      { key: 'transformation', label: 'Transformacions', icon: '↟', hasContent: () => !!data.transformationInfos?.length }, // same two-headed arrow as EditToolbar's "transform" button
      { key: 'tools', label: 'Utensilis', icon: '⚙', hasContent: () => !!data.toolInfos?.length }, // the cog
    ];
    const tabDefByKey = new Map(tabDefs.map(t => [t.key, t]));

    const tabPanes = new Map<string, Selection<HTMLDivElement, unknown, null, undefined>>();
    const tabButtons = new Map<string, Selection<HTMLButtonElement, unknown, null, undefined>>();

    const activateTab = (key: string) => {
      this.activeEditTab = key;
      for (const [k, pane] of tabPanes) {
        pane.style('display', k === key ? 'block' : 'none');
      }
      for (const [k, btn] of tabButtons) {
        const active = k === key;
        const def = tabDefByKey.get(k)!;
        const inactiveColor = def.hasContent() ? 'text-[var(--accent)]' : 'text-gray-500';
        btn
          .attr('class', active
            ? 'px-3 py-1.5 text-lg leading-none border-b-2 border-[var(--accent)] text-[var(--accent)] whitespace-nowrap'
            : `px-3 py-1.5 text-lg leading-none border-b-2 border-transparent ${inactiveColor} hover:text-gray-700`)
          .html(active ? `${def.icon} <span class="text-sm font-medium">${def.label}</span>` : def.icon);
      }
    };

    for (const tab of tabDefs) {
      const btn = tabBar.append('button')
        .attr('type', 'button')
        .attr('title', tab.label)
        .attr('aria-label', tab.label)
        .on('click', (event: MouseEvent) => {
          event.preventDefault();
          event.stopPropagation();
          activateTab(tab.key);
        });
      tabButtons.set(tab.key, btn);
    }

    const container = outer.append('div').attr('class', 'space-y-4 pt-1');
    tabPanes.set('node', container as unknown as Selection<HTMLDivElement, unknown, null, undefined>);

    const ingredientPane = outer.append('div').attr('class', 'space-y-4 pt-1');
    tabPanes.set('ingredient', ingredientPane as unknown as Selection<HTMLDivElement, unknown, null, undefined>);

    const transformationPane = outer.append('div').attr('class', 'space-y-4 pt-1');
    tabPanes.set('transformation', transformationPane as unknown as Selection<HTMLDivElement, unknown, null, undefined>);

    const toolsPane = outer.append('div').attr('class', 'space-y-4 pt-1');
    tabPanes.set('tools', toolsPane as unknown as Selection<HTMLDivElement, unknown, null, undefined>);

    activateTab(this.activeEditTab);

    // Node card — same header-band convention as the ingredient/
    // transformation/tool cards (renderEntityCard()), but with only the
    // hidden-from-elaboration toggle in it (no edit/delete — this card
    // isn't itself a catalog entity to edit/remove, it's the node).
    const nodeCard = container.append('div').attr('class', 'border border-[var(--accent-alpha-20)] rounded-md overflow-hidden');

    const nodeHeader = nodeCard.append('div');
    (nodeHeader.node() as HTMLElement).style.cssText = `
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: 4px;
      padding: 4px 6px;
      margin-bottom: 4px;
      background: var(--accent-alpha-13);
    `;
    this.renderHiddenToggleButton(
      nodeHeader.node() as HTMLElement,
      () => !!node.data.hidden,
      (hidden) => {
        node.data.hidden = hidden;
        this.onFieldChange(node.id, 'hidden', hidden);
      },
      'aquest node',
    );

    const nodeBody = nodeCard.append('div').attr('class', 'p-2 space-y-3');

    // Image section with dropdown source selector and optional uploader
    this.renderNodeImageSection(nodeBody, node);

    // Node-level name
    const nameGroup = nodeBody.append('div').attr('class', 'space-y-1');
    nameGroup.append('label')
      .attr('class', 'block text-sm font-medium text-gray-700')
      .text('Nom');
    const nameInput = nameGroup.append('input')
      .attr('type', 'text')
      .attr('class', 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')
      .attr('placeholder', 'Nom del node (sobreescriu ingredient/transformació)')
      .attr('value', data.name ?? '')
      .node() as HTMLInputElement;
    nameInput.addEventListener('input', (ev: Event) => {
      const val = nameInput.value.trim();
      node.data.name = val || undefined;
      node.name = resolveNodeTitle(node.data);
      this.onNodeModified?.(node.id);
    });
    nameInput.addEventListener('blur', () => {
      const val = nameInput.value.trim();
      node.data.name = val || undefined;
      node.name = resolveNodeTitle(node.data);
      this.commitFieldChangeDeferred(node.id, 'name', val || undefined);
    });

    // Ingredient suggestions while typing the name — only while the node
    // has none yet (same condition as "Node a ingredient" below); picking
    // one attaches it without touching the typed name, same as attaching
    // an ingredient any other way.
    if (!data.ingredientInfo?.ingredient) {
      this.renderNodeNameIngredientSuggestions(node, nameInput, nameGroup.node() as HTMLElement);
    }

    // Node-level description
    const descGroup = nodeBody.append('div').attr('class', 'space-y-1');
    descGroup.append('label')
      .attr('class', 'block text-sm font-medium text-gray-700')
      .text('Descripció');
    const descTextarea = descGroup.append('textarea')
      .attr('class', 'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')
      .attr('placeholder', 'Descripció opcional per a aquest node...')
      .attr('rows', '2')
      .text(data.description ?? '')
      .node() as HTMLTextAreaElement;
    descTextarea.addEventListener('input', (ev: Event) => {
      const val = descTextarea.value.trim();
      node.data.description = val || undefined;
      this.onNodeModified?.(node.id);
    });
    descTextarea.addEventListener('blur', () => {
      const val = descTextarea.value.trim();
      node.data.description = val || undefined;
      this.commitFieldChangeDeferred(node.id, 'description', val || undefined);
    });

    // Promote-to-ingredient button — lives on the Node tab now (was on the
    // Ingredient tab); same visibility rule: only while there's none yet.
    if (!data.ingredientInfo?.ingredient) {
      this.renderPromoteButton(nodeBody, node);
    }

    // Ingredient section — lives in its own tab
    this.renderEditableIngredientSection(ingredientPane, node);

    // Transformation section — lives in its own tab
    this.renderEditableTransformationSection(transformationPane, node);

    // Tools section — lives in its own tab
    this.renderEditableToolsSection(toolsPane, node);

    // Elaboration buttons (add/modify/remove) live in the EditToolbar now
    // — see GraphVisualizer.canElaborateSelectedNode().

  }

  /**
   * Round toggle button marking a node as excluded from the elaboration
   * (ingredients/utensilis/steps) — swaps between the open/closed eye
   * icons (eye.svg/eye-closed.svg), plus the same filled/outlined
   * background every other toggle button in the app uses (⌖/★/⛶).
   */
  /**
   * Generic eye open/closed toggle button for "excluded from elaboration"
   * — used both for a whole node (its own card header) and for a single
   * tool usage (that usage's card header, via renderToolUsageRows()). The
   * getter/setter indirection is what lets one implementation serve both:
   * the caller owns where the flag actually lives and how it's committed.
   */
  private renderHiddenToggleButton(
    container: HTMLElement,
    getHidden: () => boolean,
    setHidden: (hidden: boolean) => void,
    subjectLabel: string,
  ): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    const applyState = () => {
      const hidden = getHidden();
      setIcon(btn, hidden ? '/static/img/eye-closed.svg' : '/static/img/eye.svg', '13px');
      btn.title = hidden
        ? 'Amagat de l\'elaboració — fes clic per tornar-lo a mostrar'
        : `Amaga ${subjectLabel} de l'elaboració`;
      btn.style.cssText = hidden
        ? `
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          border: 1px solid var(--accent-alpha-40); background: var(--accent); color: #0d1117;
          font-size: 13px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease;
        `
        : `
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
          font-size: 13px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease;
        `;
    };
    applyState();
    btn.addEventListener('click', () => {
      setHidden(!getHidden());
      applyState();
    });
    // Prepend — the only child for a node's own header, but the *left*
    // slot of an entity card's header (see renderEntityCard()'s
    // entity-card-header hook), ahead of the edit/delete buttons group.
    container.prepend(btn);
  }

  /**
   * Toggle for whether a tool usage adds to the Utensilis list's "N ×"
   * count (see buildToolsHtml() in RecipeTextRenderer.ts) — e.g. a 2nd/3rd
   * knife usually isn't worth counting. Same filled/outlined convention as
   * renderHiddenToggleButton(), but the "outlined" (default) state here is
   * *counts normally*, and "filled" (exceptional) is *doesn't count* — the
   * opposite polarity from hidden, since most usages do count.
   */
  private renderCountsToggleButton(
    container: HTMLElement,
    getCounts: () => boolean,
    setCounts: (counts: boolean) => void,
  ): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '#';
    const applyState = () => {
      const counts = getCounts();
      btn.title = counts
        ? 'Es compta en el total — fes clic perquè no es compti'
        : 'No es compta en el total — fes clic perquè es torni a comptar';
      btn.style.cssText = !counts
        ? `
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          border: 1px solid var(--accent-alpha-40); background: var(--accent); color: #0d1117;
          font-size: 13px; font-weight: bold; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease;
        `
        : `
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
          font-size: 13px; font-weight: bold; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease;
        `;
    };
    applyState();
    btn.addEventListener('click', () => {
      setCounts(!getCounts());
      applyState();
    });
    container.prepend(btn);
  }

  /**
   * Toggle for whether a tool usage is a distinct physical instance rather
   * than a continuation of the same tool from an earlier step (see
   * recipe_model.RecipeTool's `fresh` doc comment) — moved here from a
   * plain checkbox in the extra-properties body so it sits with the other
   * two usage-level toggles in the card header. Glyph: "⚙" (gear) — the
   * tool glyph itself, standing in for "newly introduced" without reusing
   * the eye/number glyphs. Forced on and non-interactive when none of
   * this node's children carry the same tool — there's nothing it could
   * be continuing from in the first place (see isToolUsageImplicitlyFresh()).
   */
  private renderFreshToggleButton(
    container: HTMLElement,
    getFresh: () => boolean,
    setFresh: (fresh: boolean) => void,
    forcedOn: boolean,
  ): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.textContent = '⚙';
    const applyState = () => {
      const fresh = forcedOn ? true : getFresh();
      btn.title = forcedOn
        ? 'Nou per força: cap fill d\'aquest node fa servir el mateix utensili'
        : fresh
        ? 'Instància nova — fes clic perquè continuï un ús anterior'
        : 'Continua un ús anterior — fes clic per marcar-la com a nova';
      btn.disabled = forcedOn;
      btn.style.cssText = fresh
        ? `
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          border: 1px solid var(--accent-alpha-40); background: var(--accent); color: #0d1117;
          font-size: 16px; cursor: ${forcedOn ? 'default' : 'pointer'};
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease; opacity: ${forcedOn ? '0.6' : '1'};
        `
        : `
          width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
          border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
          font-size: 16px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.15s ease;
        `;
    };
    applyState();
    if (!forcedOn) {
      btn.addEventListener('click', () => {
        setFresh(!getFresh());
        applyState();
      });
    }
    container.prepend(btn);
  }

  // ============================================================================
  // NODE IMAGE SECTION
  // ============================================================================

  private renderNodeImageSection(
    container: Selection<any, any, any, any>,
    node: DisplayNode
  ): void {
    const data = node.data;

    const section = container.append('div').attr('class', 'space-y-2');

    const poolContainer = document.createElement('div');
    poolContainer.id = `node-image-pool-${node.id}`;
    section.node()?.appendChild(poolContainer);

    this.renderNodeImagePool(node, poolContainer, data);
  }

  /**
   * Cycle through the combined pool of the node's ingredient's images,
   * (if it has one) its transformation's images, its tools' images, plus
   * an "add new" slot. Picking an existing entry just sets the node's own
   * explicit imageUrl — no more imageSource to track. Adding a new image,
   * when the node has an ingredient, adds it to that ingredient's pool too
   * (the carry-over rule); otherwise it's a private, unpooled image for
   * this node alone — tools get the same treatment as transformations
   * here (their own pools only grow via their own Utensilis-tab editor,
   * never via a node's own image pick).
   */
  private renderNodeImagePool(
    node: DisplayNode,
    container: HTMLElement,
    data: DisplayNode['data']
  ): void {
    const ingredient = data.ingredientInfo?.ingredient;
    const transformationImages = (data.transformationInfos ?? []).flatMap(ti => ti.transformation?.images ?? []);
    const toolImages = (data.toolInfos ?? []).flatMap(ti => ti.tool.images ?? []);

    const seenUrls = new Set<string>();
    const images: ImagePoolItem[] = [];
    for (const img of [...(ingredient?.images ?? []), ...transformationImages, ...toolImages]) {
      if (!seenUrls.has(img.url)) {
        seenUrls.add(img.url);
        images.push(img);
      }
    }
    // A node's own private image (no ingredient, or a custom upload predating
    // this ingredient's/transformation's current pool) isn't necessarily part
    // of either pool above — include it too, so the cycler can still show and
    // select it, rather than incorrectly falling back to "no image".
    if (data.imageUrl && !seenUrls.has(data.imageUrl)) {
      images.unshift({ id: data.imageUrl, url: data.imageUrl });
    }

    const pool = new ImagePool({
      container,
      idPrefix: `node-image-${node.id}`,
      images,
      mode: 'pick',
      selectedUrl: data.imageUrl,
      uploadFn: async (blob) => {
        if (ingredient) {
          const added = await addIngredientImage(ingredient.id, blob);
          ingredient.images = [...(ingredient.images ?? []), added];
          this.onEntityUpdate?.('ingredient', ingredient.id, ingredient);
          return added;
        }
        // No ingredient: a private, unpooled image for this node alone.
        const formData = new FormData();
        formData.append('file', blob, 'image.jpg');
        const response = await fetch(`/api/node/${node.id}/image`, {
          method: 'POST',
          headers: { ...authService.getAuthHeaders() },
          body: formData,
        });
        if (!response.ok) throw new Error('Failed to upload image');
        const result = await response.json() as { image_url: string };
        return { id: result.image_url, url: result.image_url };
      },
      onSelect: (item) => {
        data.imageUrl = item?.url;
        this.onFieldChange(node.id, 'imageUrl', item?.url);
      },
    });
    pool.render();
  }

  // ============================================================================
  // INGREDIENT SECTION (EDIT MODE)
  // ============================================================================

  private renderEditableIngredientSection(
    container: Selection<any, any, any, any>,
    node: DisplayNode
  ): void {
    const section = container.append('div').attr('class', 'border-t pt-3 space-y-3');

    const ingInfo = node.data.ingredientInfo;
    const hasIngredient = !!ingInfo?.ingredient;

    if (!hasIngredient) {
      this.renderSearchDropdown(section, node, 'ingredient');
      return;
    }

    this.renderEntityCard(section, 'ingredient', ingInfo!.ingredient!, {
      onEntityChanged: (updated) => {
        if (node.data.ingredientInfo) node.data.ingredientInfo.ingredient = updated as Ingredient;
      },
      onRemove: () => this.clearIngredient(node),
      renderExtraProperties: (body) => this.renderIngredientNodeProperties(body, node),
    });
  }

  // ============================================================================
  // PROMOTE TO INGREDIENT
  // ============================================================================

  /**
   * Render the "Node a ingredient" button inside the ingredient section.
   * Only shown when no ingredient is assigned yet.
   */
  private renderPromoteButton(
    section: Selection<any, any, any, any>,
    node: DisplayNode
  ): void {
    const promoteGroup = section.append('div').attr('class', 'space-y-2');

    const promoteBtn = promoteGroup.append('button')
      .attr('type', 'button')
      .attr('class', 'w-full px-3 py-2 text-sm rounded-md bg-transparent border border-[var(--accent-alpha-40)] text-[#a0a0a0] hover:bg-[var(--accent-alpha-13)] hover:text-[#e0e0e0] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono')
      .text('Node a ingredient');

    promoteBtn.on('click', async () => {
      promoteBtn.attr('disabled', 'true')
        .text('Creant ingredient...');
      try {
        await this.handlePromoteToIngredient(node);
      } catch (err) {
        console.error('Failed to promote node to ingredient:', err);
        promoteBtn.attr('disabled', null)
          .text('Node a ingredient');
      }
    });

    // Small hint text below the button
    promoteGroup.append('div')
      .attr('class', 'text-xs text-[#888] font-mono text-center')
      .text('Crea un ingredient amb el nom i la imatge del node');
  }

  /**
   * Handle the "promote to ingredient" action:
   * 1. Collects the node's name and custom image
   * 2. Creates a new Ingredient entity via the API
   * 3. Assigns the ingredient to the node
   */
  private async handlePromoteToIngredient(node: DisplayNode): Promise<void> {
    // Collect data from node
    const name = node.data.name || resolveNodeTitle(node.data);

    // Create ingredient via API. Its pool starts empty — if the node already
    // has an image, saving the graph carries it over automatically (the
    // backend's create/update_recipe_node ensures a node-with-ingredient's
    // image is always part of that ingredient's pool).
    const ingredient = await createIngredient({ name });

    // Assign to node (same pattern as handleIngredientSelect)
    await this.onFieldChange(node.id, 'ingredientInfo.ingredient', ingredient);
    node.data.ingredientInfo = node.data.ingredientInfo ?? { ingredient };
    node.data.ingredientInfo.ingredient = ingredient;
    node.name = resolveNodeTitle(node.data);

    // Auto-assign the ingredient's default image if the node has none yet.
    this.autoAssignImageOnAttach(node);

    // Re-render side panel
    if (this.currentNode?.id === node.id) {
      this.renderNodeContent(node);
    }
  }

  private renderIngredientNodeProperties(
    section: Selection<any, any, any, any>,
    node: DisplayNode
  ): void {
    const ingInfo = node.data.ingredientInfo;
    if (!ingInfo?.ingredient) return;

    // Remove existing properties block if re-rendering
    section.select(`.ing-node-props-${node.id}`).remove();

    const propsDiv = section.append('div')
      .attr('class', `ing-node-props-${node.id} space-y-3`);

    // Quantity group
    const quantity = ingInfo.quantity;
    const qtyGroup = propsDiv.append('div').attr('class', 'space-y-1');

    // Label row with clear × button
    const qtyLabelRow = qtyGroup.append('div').attr('class', 'flex items-center justify-between');
    qtyLabelRow.append('label')
      .attr('class', 'block text-xs font-medium text-gray-600')
      .text('Quantitat');
    const clearQtyBtn = qtyLabelRow.append('button')
      .attr('type', 'button')
      .attr('class', 'text-gray-400 hover:text-red-500 text-sm leading-none px-1')
      .attr('title', 'Neteja la quantitat')
      .text('×')
      .node() as HTMLButtonElement;

    const inputGroup = qtyGroup.append('div').attr('class', 'flex gap-2 items-center');

    // Amount input (nullable — empty means unset) — fixed narrow width
    // rather than flexing to fill the row, so the unit <select> below (whose
    // own width is driven by its widest option's text) is the one that
    // shrinks to fit when space is tight, instead of pushing the tab wider.
    const amountInput = inputGroup.append('input')
      .attr('type', 'number')
      .attr('class', 'w-16 flex-shrink-0 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')
      .attr('step', '1.0')
      .attr('min', '0')
      .attr('placeholder', '—')
      .attr('value', quantity?.amount ?? '')
      .node() as HTMLInputElement;

    // Unit select
    const unitSelect = inputGroup.append('select')
      .attr('class', 'flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')
      .node() as HTMLSelectElement;

    // Add empty option for unset
    const emptyOption = document.createElement('option');
    emptyOption.value = '';
    emptyOption.textContent = '—';
    emptyOption.selected = !quantity;
    unitSelect.appendChild(emptyOption);

    // Populate from unit cache (must be loaded before edit mode)
    const allUnits = unitCache.getAll();
    if (allUnits.length > 0) {
      allUnits.forEach(unit => {
        const option = document.createElement('option');
        option.value = unit.id;
        option.textContent = unit.plural;
        option.selected = quantity?.unit === unit.id;
        option.title = `${unit.abbreviation} – ${unit.singular}`;
        unitSelect.appendChild(option);
      });
    } else {
      // Fallback: use well-known built-in IDs
      const fallbackUnits = [
        { id: BUILTIN_UNIT_IDS.KILOS, label: 'quilos' },
        { id: BUILTIN_UNIT_IDS.LITERS, label: 'litres' },
        { id: BUILTIN_UNIT_IDS.UNIT, label: 'unitats' },
      ];
      fallbackUnits.forEach(u => {
        const option = document.createElement('option');
        option.value = u.id;
        option.textContent = u.label;
        option.selected = quantity?.unit === u.id;
        unitSelect.appendChild(option);
      });
    }

    // Cog button to open unit editor
    const cogBtn = document.createElement('button');
    cogBtn.type = 'button';
    cogBtn.className = 'flex-shrink-0 px-2 py-1 text-sm border border-gray-300 rounded-md text-[#888] hover:text-[var(--accent)] hover:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-colors';
    cogBtn.title = 'Gestiona les unitats';
    // SVG cog icon
    cogBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`;
    cogBtn.addEventListener('click', () => this.openUnitEditor());

    // Append cog button after the select
    inputGroup.node()?.appendChild(cogBtn);

    const clearQuantity = () => {
      amountInput.value = '';
      unitSelect.value = '';
      this.onFieldChange(node.id, 'ingredientInfo.quantity', null);
      node.data.ingredientInfo!.quantity = undefined;
    };

    const saveQuantity = () => {
      const amount = parseFloat(amountInput.value);
      const unit = unitSelect.value;
      if (!isNaN(amount) && unit) {
        this.commitFieldChangeDeferred(node.id, 'ingredientInfo.quantity', { amount, unit });
        node.data.ingredientInfo!.quantity = { amount, unit };
      } else {
        this.commitFieldChangeDeferred(node.id, 'ingredientInfo.quantity', null);
        node.data.ingredientInfo!.quantity = undefined;
      }
    };

    amountInput.addEventListener('blur', saveQuantity);
    unitSelect.addEventListener('change', saveQuantity);
    clearQtyBtn.addEventListener('click', clearQuantity);

    // Notes (description) — with × clear button aligned to label
    const notesGroup = propsDiv.append('div').attr('class', 'space-y-1');
    const notesLabelRow = notesGroup.append('div').attr('class', 'flex items-center justify-between');
    notesLabelRow.append('label')
      .attr('class', 'block text-xs font-medium text-gray-600')
      .text('Notes');
    notesLabelRow.append('button')
      .attr('type', 'button')
      .attr('class', 'text-gray-400 hover:text-red-500 text-sm leading-none px-1')
      .attr('title', 'Neteja les notes')
      .text('×')
      .on('click', () => {
        notesTextarea.value = '';
        this.onFieldChange(node.id, 'ingredientInfo.description', undefined);
        node.data.ingredientInfo!.description = undefined;
      });
    const notesTextarea = notesGroup.append('textarea')
      .attr('class', 'w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')
      .attr('placeholder', 'Notes sobre aquest ingredient a la recepta...')
      .attr('rows', '2')
      .text(ingInfo.description ?? '')
      .node() as HTMLTextAreaElement;
    notesTextarea.addEventListener('blur', () => {
      const val = notesTextarea.value.trim();
      this.commitFieldChangeDeferred(node.id, 'ingredientInfo.description', val || undefined);
      node.data.ingredientInfo!.description = val || undefined;
    });
  }

  // ============================================================================
  // TRANSFORMATION SECTION (EDIT MODE)
  // ============================================================================

  /**
   * A node can carry several transformations, in a user-defined order
   * (e.g. "talla" then "pela") — rendered as a reorderable *list* (drag to
   * reorder, mirroring List View's own sibling drag-and-drop interaction)
   * rather than a single slot, same reasoning the Utensilis tab's tool
   * list uses, see renderEditableToolsSection()'s doc comment.
   */
  private renderEditableTransformationSection(
    container: Selection<any, any, any, any>,
    node: DisplayNode
  ): void {
    const section = container.append('div').attr('class', 'border-t pt-3 space-y-3');

    const list = section.append('div').attr('class', 'space-y-2');
    this.renderTransformationUsageRows(list, node);
    this.renderAddTransformationSearchDropdown(section, node);
  }

  private commitTransformationInfosChange(node: DisplayNode): void {
    this.onFieldChange(node.id, 'transformationInfos', node.data.transformationInfos ?? []);
  }

  private renderTransformationUsageRows(listContainer: Selection<any, any, any, any>, node: DisplayNode): void {
    listContainer.html('');
    const transformationInfos = node.data.transformationInfos ?? [];

    // Pointer Events (not native HTML5 drag-and-drop) so this works on
    // touch too — mobile browsers never fire dragstart/dragover/drop for
    // arbitrary elements, only for a handful of built-ins (links, images),
    // so native DnD is desktop-only here. Pointer events unify mouse/touch/
    // pen into one code path. Simple index-swap-on-release, unlike List
    // View's ListDragController (reparenting across a graph, long-press-
    // to-disambiguate-from-scroll, auto-scroll): this list is short and
    // flat, so that machinery would be overkill.
    const rowEls: HTMLElement[] = [];
    let dragSrcIndex: number | null = null;
    let hoverIndex: number | null = null;

    const clearHoverStyle = () => {
      for (const el of rowEls) {
        el.style.borderTop = '';
        el.style.borderBottom = '';
      }
    };
    // hoverIndex means "insert before row[hoverIndex]" — rowEls.length
    // itself is a valid value, meaning "insert after everything".
    const updateHover = (clientY: number) => {
      let next = rowEls.length;
      for (let i = 0; i < rowEls.length; i++) {
        const rect = rowEls[i].getBoundingClientRect();
        if (clientY < rect.top + rect.height / 2) {
          next = i;
          break;
        }
      }
      if (next === hoverIndex) return;
      hoverIndex = next;
      clearHoverStyle();
      if (hoverIndex < rowEls.length) {
        rowEls[hoverIndex].style.borderTop = '2px solid var(--accent)';
      } else if (rowEls.length > 0) {
        rowEls[rowEls.length - 1].style.borderBottom = '2px solid var(--accent)';
      }
    };
    const endDrag = (commit: boolean) => {
      clearHoverStyle();
      if (dragSrcIndex !== null) rowEls[dragSrcIndex].style.opacity = '';
      if (commit && dragSrcIndex !== null && hoverIndex !== null && hoverIndex !== dragSrcIndex) {
        const infos = node.data.transformationInfos ?? [];
        const [moved] = infos.splice(dragSrcIndex, 1);
        // Removing the source first shifts later indices down by one —
        // account for that when the target was after it.
        const insertAt = hoverIndex > dragSrcIndex ? hoverIndex - 1 : hoverIndex;
        infos.splice(insertAt, 0, moved);
        node.data.transformationInfos = infos;
        node.name = resolveNodeTitle(node.data);
        this.commitTransformationInfosChange(node);
        this.renderTransformationUsageRows(listContainer, node);
      }
      dragSrcIndex = null;
      hoverIndex = null;
    };

    transformationInfos.forEach((trafoInfo, index) => {
      // Every entry added through this editor has a transformation (the
      // search dropdown always yields one) — skip defensively rather than
      // rendering a card with nothing to show, for an entry that somehow
      // doesn't (e.g. a description-only row created outside the editor).
      if (!trafoInfo.transformation) return;

      const rowWrapper = listContainer.append('div');
      const rowEl = rowWrapper.node() as HTMLElement;
      rowEls.push(rowEl);

      this.renderEntityCard(rowWrapper, 'transformation', trafoInfo.transformation, {
        onEntityChanged: (updated) => {
          trafoInfo.transformation = updated as Transformation;
          this.commitTransformationInfosChange(node);
        },
        onRemove: () => {
          const infos = node.data.transformationInfos ?? [];
          infos.splice(index, 1);
          node.data.transformationInfos = infos;
          node.name = resolveNodeTitle(node.data);
          this.commitTransformationInfosChange(node);
          if (this.currentNode?.id === node.id) this.renderNodeContent(node);
        },
        renderExtraProperties: (body) => this.renderTransformationUsageExtraProperties(
          body, node, trafoInfo, index === transformationInfos.length - 1,
        ),
      });

      // Drag handle — prepended into the card's header (the one place
      // with nothing else on its left, see renderEntityCard()'s
      // entity-card-header hook), the only drag source. Starting a drag
      // from on top of the card's own buttons/image/inputs is unreliable,
      // so the grip is the one guaranteed-plain target.
      const header = rowEl.querySelector('.entity-card-header');
      if (header) {
        const grip = document.createElement('span');
        grip.textContent = '⣿';
        grip.title = 'Arrossega per reordenar';
        grip.style.cssText = `
          cursor: grab;
          font-size: 13px;
          color: var(--accent-alpha-40);
          user-select: none;
          touch-action: none;
          flex-shrink: 0;
        `;
        grip.addEventListener('pointerdown', (e: PointerEvent) => {
          e.preventDefault();
          dragSrcIndex = index;
          hoverIndex = index;
          rowEl.style.opacity = '0.4';
          grip.setPointerCapture(e.pointerId);
        });
        grip.addEventListener('pointermove', (e: PointerEvent) => {
          if (dragSrcIndex === null) return;
          updateHover(e.clientY);
        });
        grip.addEventListener('pointerup', () => endDrag(true));
        grip.addEventListener('pointercancel', () => endDrag(false));
        header.prepend(grip);
      }
    });
  }

  /**
   * The Transformació tab's own "extra properties" — auto-detected render
   * template parameters, plus notes — mirroring renderIngredientNodeProperties()/
   * renderToolUsageExtraProperties()'s role for the other tabs. One usage
   * at a time; the node's children (and everything else `self.*` exposes)
   * are shared across every usage on this node, only `parameters`/
   * `description` are this usage's own.
   */
  private renderTransformationUsageExtraProperties(
    section: Selection<any, any, any, any>,
    node: DisplayNode,
    trafoInfo: TransformationInfo,
    // A node's transformation usages are strictly sequential — the
    // backend forces attentionDuration = duration on every usage
    // except the node's own last one (see write.py's normalization),
    // so there's nothing meaningful to show or edit for an earlier
    // usage; the Attention block below is gated on this.
    isLastUsage: boolean,
  ): void {
    // Parameters section (auto-detected from render template)
    this.renderTransformationParametersSection(section, node, trafoInfo);

    // Duration — composite two-field input ("‹ [1] h. [30] min. ›"),
    // converted to/from seconds. See DurationInput.ts's doc comment.
    const durationGroup = section.append('div').attr('class', 'space-y-1 pt-2');
    const durationLabelRow = durationGroup.append('div').attr('class', 'flex items-center justify-between');
    durationLabelRow.append('label')
      .attr('class', 'block text-xs font-medium text-gray-600')
      .text('Durada');
    const durationClearBtn = durationLabelRow.append('button')
      .attr('type', 'button')
      .attr('class', 'text-gray-400 hover:text-red-500 text-sm leading-none px-1')
      .attr('title', 'Neteja la durada')
      .text('×')
      .node() as HTMLButtonElement;
    // Declared before durationHandle is even created so Duration's own
    // onChange (defined right below) can reach it — by the time that
    // callback actually RUNS (a later user interaction), the Attention
    // block below has long since been created (or not, if there was no
    // duration to show it for) and this holds whatever it's going to.
    let attentionHandle: DurationInputHandle | undefined;

    const durationHandle = renderDurationInput(
      durationGroup.node() as HTMLElement,
      trafoInfo.duration,
      (seconds) => {
        trafoInfo.duration = seconds;
        // Keep attentionDuration <= duration from this side too: lowering
        // duration below it pulls it down to match (never meaningful
        // above the duration it's a portion of); losing the duration
        // entirely clears it outright, same as the Attention block
        // disappearing below once there's no duration left for it to be
        // a fraction of. Raising duration never needs to touch it. Both
        // branches also push the new value into attentionHandle directly
        // — commitFieldChangeDeferred() below persists the change but
        // doesn't rebuild this panel, so without this the OTHER field
        // would just sit there showing its old, now-stale value.
        attentionHandle?.setPlaceholderSeconds(seconds);
        if (!seconds) {
          trafoInfo.attentionDuration = undefined;
          attentionHandle?.setValue(undefined);
          removeAttentionBlock();
        } else {
          // Duration just became non-zero — the Attention block may not
          // exist yet at all (it's gated on duration being truthy AT THE
          // TIME this panel was built; editing duration from 0 live,
          // rather than reopening the editor, used to leave it missing
          // until the next full rebuild — see buildAttentionBlock()'s
          // own doc comment). A no-op once it already exists.
          buildAttentionBlock();
          if (trafoInfo.attentionDuration != null && trafoInfo.attentionDuration > seconds) {
            trafoInfo.attentionDuration = seconds;
            attentionHandle?.setValue(seconds);
          }
        }
        // Deferred, not commitTransformationInfosChange(): this fires from
        // a field's blur handler, which — same reasoning as the Notes
        // textarea just below — happens *before* a click already in
        // flight on another control (e.g. one of the ‹/› arrows) actually
        // fires. Committing synchronously here would rebuild this panel
        // and remove that control from the DOM between the click's
        // mousedown and click events, silently swallowing it.
        this.commitFieldChangeDeferred(node.id, 'transformationInfos', node.data.transformationInfos ?? []);
      },
      {
        inputClassName: 'w-16 flex-shrink-0 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
        labelClassName: 'text-sm text-gray-600',
      },
    );
    durationClearBtn.addEventListener('click', () => durationHandle.clear());

    // Attention time — how much of the duration needs the preparer's
    // hands before they're free to start the next step (see
    // recipe_renderer/schedule.ts): unset means the whole duration does
    // (not parallelizable). Only shown once there's a nonzero duration
    // for it to be a portion of, AND only on a node's own LAST
    // transformation usage — a node's usages are strictly sequential,
    // so an earlier usage can never be parallelizable (the backend
    // forces its attentionDuration = duration regardless of what's
    // sent; see write.py), leaving nothing meaningful to show here.
    // Explicit 0 (fully parallelizable, no attention needed at all) is
    // kept distinct from unset — only the × button clears it;
    // typing/landing on 0 stays 0 (zeroIsValid below). While unset, the
    // input shows the duration's own value grayed out (placeholder
    // Seconds — a native input placeholder, not a real value) rather
    // than blank or a bare "0": a visual "fully attended by default"
    // hint, not an actual commit — attentionDuration itself stays
    // undefined until the user actually edits or clears the field.
    //
    // Built into a persistent, initially-empty container (rather than
    // appended straight into `section` only when eligible) so Duration
    // going from 0 to non-zero — edited live, not just found that way
    // when the editor was opened — can show this block immediately via
    // buildAttentionBlock() (see durationHandle's onChange above),
    // instead of requiring the whole panel to be rebuilt (closing and
    // reopening the editor) before it appears.
    const attentionContainer = section.append('div');

    const buildAttentionBlock = (): void => {
      if (!isLastUsage || attentionHandle) return; // ineligible, or already built
      const attentionGroup = attentionContainer.append('div').attr('class', 'space-y-1 pt-2');
      const attentionLabelRow = attentionGroup.append('div').attr('class', 'flex items-center justify-between');
      attentionLabelRow.append('label')
        .attr('class', 'block text-xs font-medium text-gray-600')
        .text('Temps d\'atenció');
      const attentionClearBtn = attentionLabelRow.append('button')
        .attr('type', 'button')
        .attr('class', 'text-gray-400 hover:text-red-500 text-sm leading-none px-1')
        .attr('title', 'Neteja el temps d\'atenció')
        .text('×')
        .node() as HTMLButtonElement;
      attentionHandle = renderDurationInput(
        attentionGroup.node() as HTMLElement,
        trafoInfo.attentionDuration,
        (seconds) => {
          trafoInfo.attentionDuration = seconds;
          // Mirror of the Duration-side clamp above: setting attention
          // ABOVE the current duration raises the duration to match,
          // rather than clamping attention back down — attention is the
          // field being driven here, so it wins. Push the raised value
          // into durationHandle directly, same reasoning as the other
          // direction: nothing else refreshes it on this same tick.
          if (seconds != null && (!trafoInfo.duration || seconds > trafoInfo.duration)) {
            trafoInfo.duration = seconds;
            durationHandle.setValue(seconds);
          }
          this.commitFieldChangeDeferred(node.id, 'transformationInfos', node.data.transformationInfos ?? []);
        },
        {
          zeroIsValid: true,
          placeholderSeconds: trafoInfo.duration,
          inputClassName: 'w-16 flex-shrink-0 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
          labelClassName: 'text-sm text-gray-600',
        },
      );
      attentionClearBtn.addEventListener('click', () => attentionHandle?.clear());
    };

    /** Mirror of buildAttentionBlock() — duration dropping back to 0
     *  (there's no longer anything for attention to be a portion of). */
    const removeAttentionBlock = (): void => {
      attentionContainer.selectAll('*').remove();
      attentionHandle = undefined;
    };

    if (trafoInfo.duration && isLastUsage) buildAttentionBlock();

    // Notes (description) — with × clear button aligned to label
    const notesGroup = section.append('div').attr('class', 'space-y-1 pt-2');
    const notesLabelRow = notesGroup.append('div').attr('class', 'flex items-center justify-between');
    notesLabelRow.append('label')
      .attr('class', 'block text-xs font-medium text-gray-600')
      .text('Notes');
    notesLabelRow.append('button')
      .attr('type', 'button')
      .attr('class', 'text-gray-400 hover:text-red-500 text-sm leading-none px-1')
      .attr('title', 'Neteja les notes')
      .text('×')
      .on('click', () => {
        notesTextarea.value = '';
        trafoInfo.description = undefined;
        this.commitTransformationInfosChange(node);
      });
    const notesTextarea = notesGroup.append('textarea')
      .attr('class', 'w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')
      .attr('placeholder', 'Notes sobre aquesta transformació a la recepta...')
      .attr('rows', '2')
      .text(trafoInfo.description ?? '')
      .node() as HTMLTextAreaElement;
    notesTextarea.addEventListener('blur', () => {
      const val = notesTextarea.value.trim();
      trafoInfo.description = val || undefined;
      this.commitFieldChangeDeferred(node.id, 'transformationInfos', node.data.transformationInfos ?? []);
    });
  }

  /**
   * "Add transformation" — a search dropdown that *appends* a new usage to
   * the list rather than replacing a single slot, mirroring
   * renderAddToolSearchDropdown() (see that method's doc comment).
   */
  private renderAddTransformationSearchDropdown(
    section: Selection<any, any, any, any>,
    node: DisplayNode
  ): void {
    const wrapper = section.append('div').attr('class', 'search-dropdown-wrapper relative');
    const searchContainer = wrapper.append('div').attr('class', 'space-y-2');

    const input = searchContainer.append('input')
      .attr('type', 'text')
      .attr('placeholder', 'Afegeix una transformació...')
      .attr('class', 'w-full px-3 py-2 border border-[#333] bg-[#0d1117] text-[#e0e0e0] placeholder-[#666] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)]')
      .node() as HTMLInputElement;

    const dropdownContainer = document.createElement('div');
    dropdownContainer.id = `transformation-add-dropdown-${node.id}`;
    dropdownContainer.className =
      'w-full mt-1 bg-[#1e1e2e] border border-[var(--accent-alpha-20)] rounded-md text-[#e0e0e0] max-h-[180px] overflow-y-auto shadow-lg';
    dropdownContainer.style.display = 'none';
    (searchContainer.node() as HTMLElement).appendChild(dropdownContainer);

    const createButton = searchContainer.append('button')
      .attr('type', 'button')
      .attr('class', 'sticky bottom-0 w-full px-3 py-2 text-sm rounded-b-md border-t border-[var(--accent-alpha-27)] bg-[var(--accent)] text-[#0d1117] hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono font-bold')
      .style('display', 'none')
      .node() as HTMLButtonElement;

    const inlineContainer = document.createElement('div');
    inlineContainer.id = `transformation-add-create-${node.id}`;
    section.node()?.appendChild(inlineContainer);

    const appendTransformation = (transformation: Transformation) => {
      const infos = node.data.transformationInfos ?? [];
      infos.push({ transformation, duration: transformation.typicalDuration });
      node.data.transformationInfos = infos;
      node.name = resolveNodeTitle(node.data);
      this.commitTransformationInfosChange(node);
      this.autoAssignImageOnAttach(node);
      input.value = '';
      if (this.currentNode?.id === node.id) this.renderNodeContent(node);
    };

    const searchDropdown = new SearchDropdown(
      'transformation',
      input,
      dropdownContainer,
      createButton,
      (selected) => appendTransformation(selected as Transformation),
      (query) => {
        wrapper.style('display', 'none');
        new InlineEntityEditor({
          entityType: 'transformation',
          container: inlineContainer,
          initialName: query,
          onCreated: (created) => {
            inlineContainer.innerHTML = '';
            wrapper.style('display', '');
            appendTransformation(created as Transformation);
          },
          onCancelled: () => {
            inlineContainer.innerHTML = '';
            wrapper.style('display', '');
          },
        }).render();
      },
      false, // appending, not replacing a slot — don't overwrite the input with the picked name
      wrapper.node() as HTMLElement
    );
    searchDropdown.init();
    this.searchDropdowns.push(searchDropdown);
  }

  // ============================================================================
  // TOOLS SECTION (EDIT MODE)
  //
  // Unlike the ingredient/transformation tabs (a single optional slot), a
  // node can use several tools at once — so this tab manages a *list*
  // (add/remove rows) rather than one search dropdown + inline editor.
  // ============================================================================

  private renderEditableToolsSection(
    container: Selection<any, any, any, any>,
    node: DisplayNode
  ): void {
    const section = container.append('div').attr('class', 'border-t pt-3 space-y-3');

    const list = section.append('div').attr('class', 'space-y-2');
    this.renderToolUsageRows(list, node);
    this.renderAddToolSearchDropdown(section, node);
  }

  private commitToolInfosChange(node: DisplayNode): void {
    this.onFieldChange(node.id, 'toolInfos', node.data.toolInfos ?? []);
  }

  /**
   * Whether a tool usage is implicitly the first instance in its lineage —
   * none of this node's children carry the same tool (by id), so there's
   * nothing it could be "continuing" from. See recipe_model.RecipeTool's
   * doc comment: `fresh` only means something as an override when a
   * continuation is otherwise possible; here there isn't one.
   */
  private isToolUsageImplicitlyFresh(node: DisplayNode, toolId: string): boolean {
    if (!this.getNodeById) return false;
    for (const childId of node.data.children ?? []) {
      const child = this.getNodeById(childId);
      if (child?.data.toolInfos?.some(ti => ti.tool.id === toolId)) return false;
    }
    return true;
  }

  private renderToolUsageRows(listContainer: Selection<any, any, any, any>, node: DisplayNode): void {
    listContainer.html('');
    const toolInfos = node.data.toolInfos ?? [];

    toolInfos.forEach((toolInfo, index) => {
      const rowWrapper = listContainer.append('div');
      const rowEl = rowWrapper.node() as HTMLElement;

      this.renderEntityCard(rowWrapper, 'tool', toolInfo.tool, {
        onEntityChanged: (updated) => {
          toolInfo.tool = updated as Tool;
          this.commitToolInfosChange(node);
        },
        onRemove: () => {
          const infos = node.data.toolInfos ?? [];
          infos.splice(index, 1);
          node.data.toolInfos = infos;
          node.name = resolveNodeTitle(node.data);
          this.commitToolInfosChange(node);
          if (this.currentNode?.id === node.id) this.renderNodeContent(node);
        },
        renderExtraProperties: (body) => this.renderToolUsageExtraProperties(body, node, toolInfo),
      });

      // Three usage-level toggles in this one row's own card header, left
      // slot (unlike the transformation list, nothing else wants that
      // slot here) — hidden/counts/fresh, left to right, with edit/delete
      // staying on the right (renderEntityCard()'s buttonsGroup). The
      // header itself is `justify-content: space-between` (see
      // renderEntityCard()'s doc comment), which only looks right with
      // exactly two children, so the three toggles are grouped into one
      // wrapper — otherwise space-between would spread all of them (plus
      // buttonsGroup) evenly across the header instead of keeping them
      // together on the left. Each helper prepends into that wrapper, so
      // they're called in reverse (fresh, counts, hidden) to land in that
      // left-to-right order.
      const header = rowEl.querySelector('.entity-card-header');
      if (header) {
        const leftGroup = document.createElement('div');
        leftGroup.style.cssText = 'display: flex; align-items: center; gap: 4px; flex-shrink: 0;';
        header.prepend(leftGroup);

        const implicitlyFresh = this.isToolUsageImplicitlyFresh(node, toolInfo.tool.id);
        this.renderFreshToggleButton(
          leftGroup,
          () => !!toolInfo.fresh,
          (fresh) => {
            toolInfo.fresh = fresh;
            this.commitToolInfosChange(node);
          },
          implicitlyFresh,
        );
        this.renderCountsToggleButton(
          leftGroup,
          () => toolInfo.counts !== false,
          (counts) => {
            toolInfo.counts = counts;
            this.commitToolInfosChange(node);
          },
        );
        this.renderHiddenToggleButton(
          leftGroup,
          () => !!toolInfo.hidden,
          (hidden) => {
            toolInfo.hidden = hidden;
            this.commitToolInfosChange(node);
          },
          'aquest utensili',
        );
      }
    });
  }

  /**
   * The Utensilis tab's own "extra properties" — just the notes textarea;
   * `fresh` lives as a header toggle now (renderFreshToggleButton(), wired
   * from renderToolUsageRows()), mirroring hidden/counts. Role otherwise
   * same as renderIngredientNodeProperties()/renderTransformationNodeProperties()
   * for the other two tabs.
   */
  private renderToolUsageExtraProperties(
    body: Selection<any, any, any, any>,
    node: DisplayNode,
    toolInfo: ToolInfo,
  ): void {
    const descTextarea = document.createElement('textarea');
    descTextarea.className = 'w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
    descTextarea.placeholder = 'Notes sobre aquest ús...';
    descTextarea.rows = 1;
    descTextarea.value = toolInfo.description ?? '';
    descTextarea.addEventListener('blur', () => {
      const val = descTextarea.value.trim();
      toolInfo.description = val || undefined;
      this.commitFieldChangeDeferred(node.id, 'toolInfos', node.data.toolInfos);
    });
    body.node()?.appendChild(descTextarea);
  }

  /**
   * "Add tool" — a search dropdown that *appends* a new usage to the list
   * rather than replacing a single slot (unlike renderSearchDropdown(),
   * which the ingredient/transformation tabs use).
   */
  private renderAddToolSearchDropdown(
    section: Selection<any, any, any, any>,
    node: DisplayNode
  ): void {
    const wrapper = section.append('div').attr('class', 'search-dropdown-wrapper relative');
    const searchContainer = wrapper.append('div').attr('class', 'space-y-2');

    const input = searchContainer.append('input')
      .attr('type', 'text')
      .attr('placeholder', 'Afegeix un utensili...')
      .attr('class', 'w-full px-3 py-2 border border-[#333] bg-[#0d1117] text-[#e0e0e0] placeholder-[#666] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)]')
      .node() as HTMLInputElement;

    const dropdownContainer = document.createElement('div');
    dropdownContainer.id = `tool-add-dropdown-${node.id}`;
    dropdownContainer.className =
      'w-full mt-1 bg-[#1e1e2e] border border-[var(--accent-alpha-20)] rounded-md text-[#e0e0e0] max-h-[180px] overflow-y-auto shadow-lg';
    dropdownContainer.style.display = 'none';
    (searchContainer.node() as HTMLElement).appendChild(dropdownContainer);

    const createButton = searchContainer.append('button')
      .attr('type', 'button')
      .attr('class', 'sticky bottom-0 w-full px-3 py-2 text-sm rounded-b-md border-t border-[var(--accent-alpha-27)] bg-[var(--accent)] text-[#0d1117] hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono font-bold')
      .style('display', 'none')
      .node() as HTMLButtonElement;

    const inlineContainer = document.createElement('div');
    inlineContainer.id = `tool-add-create-${node.id}`;
    section.node()?.appendChild(inlineContainer);

    const appendTool = (tool: Tool) => {
      const infos = node.data.toolInfos ?? [];
      // Reuse an existing row for the same catalog tool rather than
      // duplicating it — picking it again is a no-op, not a second usage.
      if (infos.some(ti => ti.tool.id === tool.id)) return;
      infos.push({ tool, hidden: tool.defaultHidden, counts: tool.defaultCounts });
      node.data.toolInfos = infos;
      node.name = resolveNodeTitle(node.data);
      this.commitToolInfosChange(node);
      this.autoAssignImageOnAttach(node);
      input.value = '';
      if (this.currentNode?.id === node.id) this.renderNodeContent(node);
    };

    const searchDropdown = new SearchDropdown(
      'tool',
      input,
      dropdownContainer,
      createButton,
      (selected) => appendTool(selected as Tool),
      (query) => {
        wrapper.style('display', 'none');
        new InlineEntityEditor({
          entityType: 'tool',
          container: inlineContainer,
          initialName: query,
          onCreated: (created) => {
            inlineContainer.innerHTML = '';
            wrapper.style('display', '');
            appendTool(created as Tool);
          },
          onCancelled: () => {
            inlineContainer.innerHTML = '';
            wrapper.style('display', '');
          },
        }).render();
      },
      false, // appending, not replacing a slot — don't overwrite the input with the picked name
      wrapper.node() as HTMLElement
    );
    searchDropdown.init();
    this.searchDropdowns.push(searchDropdown);
  }

  // ============================================================================
  // TRANSFORMATION PARAMETERS SECTION
  // ============================================================================

  /**
   * Render parameter input fields for transformation parameters.
   * Extracts `params.*` references from the render template and auto-generates
   * corresponding input fields.
   */
  private renderTransformationParametersSection(
    propsDiv: Selection<any, any, any, any>,
    node: DisplayNode,
    trafoInfo: TransformationInfo
  ): void {
    const template = trafoInfo.transformation?.renderTemplate;
    if (!template) return;

    // Extract params.* references from the template
    const paramNames = this.extractParamNames(template);
    if (paramNames.length === 0) return;

    const paramsGroup = propsDiv.append('div').attr('class', 'space-y-2');
    paramsGroup.append('label')
      .attr('class', 'block text-xs font-medium text-gray-600')
      .text('Paràmetres de la transformació');

    const currentParams: Record<string, unknown> = trafoInfo.parameters ?? {};

    for (const paramName of paramNames) {
      const paramGroup = paramsGroup.append('div').attr('class', 'space-y-0.5');

      // Human-readable label from param name
      const label = this.paramNameToLabel(paramName);
      paramGroup.append('label')
        .attr('class', 'block text-xs text-gray-500 font-mono')
        .text(label);

      // Flex row: input (or node pill, when the value is a node id) + pick
      // node button
      const inputRow = paramGroup.append('div').attr('class', 'flex gap-1 items-center');

      const input = inputRow.append('input')
        .attr('type', 'text')
        .attr('class', 'flex-1 min-w-0 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500')
        .attr('placeholder', label)
        .attr('value', currentParams[paramName] !== undefined ? String(currentParams[paramName]) : '')
        .node() as HTMLInputElement;

      // Node pill — shown instead of the input whenever its value matches
      // an existing node id (e.g. one picked via ◎), so a param holding a
      // node reference reads as that node (thumbnail + name) rather than
      // a bare id. Hidden while actively picking (see pickBtn below) so
      // the live hover-preview text stays visible there instead.
      const pill = document.createElement('div');
      pill.title = 'Fes clic per esborrar';
      pill.className = 'flex-1 min-w-0 flex items-center gap-1.5 px-2 py-1 rounded-md border border-[var(--accent-alpha-40)] bg-[var(--accent-alpha-08)] cursor-pointer';
      const pillImg = document.createElement('img');
      pillImg.className = 'w-5 h-5 rounded-full object-cover flex-shrink-0';
      pillImg.alt = '';
      pillImg.addEventListener('error', () => { pillImg.style.display = 'none'; });
      pill.appendChild(pillImg);
      const pillName = document.createElement('span');
      pillName.className = 'text-sm font-mono text-[#e0e0e0] truncate';
      pill.appendChild(pillName);
      (inputRow.node() as HTMLElement).insertBefore(pill, input);

      const updatePillOrInput = () => {
        const val = input.value.trim();
        const matchedNode = val && this.pickInputRef !== input ? this.getNodeById?.(val) : undefined;
        if (matchedNode) {
          input.style.display = 'none';
          pill.style.display = 'flex';
          const imgUrl = getNodeImageUrl(matchedNode.data);
          pillImg.style.display = imgUrl ? '' : 'none';
          if (imgUrl) pillImg.src = imgUrl;
          pillName.textContent = matchedNode.name;
        } else {
          input.style.display = '';
          pill.style.display = 'none';
        }
      };
      updatePillOrInput();

      // Clicking the pill clears the param — re-picking a different node
      // stays available via ◎ right next to it either way.
      pill.addEventListener('click', () => {
        input.value = '';
        const params = { ...(trafoInfo.parameters ?? {}) };
        delete params[paramName];
        trafoInfo.parameters = Object.keys(params).length > 0 ? params : undefined;
        this.commitTransformationInfosChange(node);
        updatePillOrInput();
        input.focus();
      });

      // Pick node button
      const pickBtn = inputRow.append('button')
        .attr('type', 'button')
        .attr('class', 'flex-shrink-0 px-2 py-1 text-sm border border-gray-300 rounded-md hover:border-[var(--accent)] hover:text-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] text-gray-400 bg-transparent transition-colors pick-node-btn')
        .attr('title', 'Tria un node del graf per inserir-ne el ID')
        .text('◎')
        .node() as HTMLButtonElement;

      // Pick button click handler
      pickBtn.addEventListener('click', (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        // If already picking for this param, cancel
        if (this.pickNodeCancel) {
          this.pickNodeCancel();
          this.pickNodeCancel = null;
          this.pickInputRef = null;
          this.pickOriginalValue = undefined;
          pickBtn.style.color = '';
          updatePillOrInput();
          return;
        }

        // Start pick node mode — use inline style for icon color
        // (avoids CSS specificity issues with text-gray-400)
        pickBtn.style.color = 'var(--accent)';

        // Store input ref for hover preview
        this.pickInputRef = input;
        this.pickOriginalValue = undefined;
        updatePillOrInput(); // reveal the plain input while picking

        try {
          this.pickNodeCancel = this.onStartPickNode!(node.id, paramName, (pickedNodeId: string | null) => {
            this.pickNodeCancel = null;
            this.pickInputRef = null;
            this.pickOriginalValue = undefined;
            pickBtn.style.color = '';
            if (pickedNodeId) {
              // Fill the input with the picked node's ID
              input.value = pickedNodeId;
              const params = { ...(trafoInfo.parameters ?? {}) };
              params[paramName] = pickedNodeId;
              trafoInfo.parameters = Object.keys(params).length > 0 ? params : undefined;
              this.commitTransformationInfosChange(node);
            }
            updatePillOrInput();
          });
        } catch (err) {
          console.error('[PICK] Error calling onStartPickNode:', err);
        }
      });

      // Keep existing blur handler for manual input
      input.addEventListener('blur', () => {
        const val = input.value.trim();
        const params = { ...(trafoInfo.parameters ?? {}) };
        if (val) {
          // Try to parse as number if applicable
          params[paramName] = this.isNumeric(val) ? parseFloat(val) : val;
        } else {
          delete params[paramName];
        }
        trafoInfo.parameters = Object.keys(params).length > 0 ? params : undefined;
        this.commitFieldChangeDeferred(node.id, 'transformationInfos', node.data.transformationInfos ?? []);
        updatePillOrInput();
      });
    }
  }

  /**
   * Extract unique param names from a render template.
   * Matches `params.xxx` or `params.xxx.yyy` patterns.
   */
  private extractParamNames(template: string): string[] {
    const paramsSet = new Set<string>();
    // Match params.xxx where xxx is a valid identifier
    const regex = /params\.([_a-zA-Z][_a-zA-Z0-9]*)/g;
    let match;
    while ((match = regex.exec(template)) !== null) {
      paramsSet.add(match[1]);
    }
    return Array.from(paramsSet).sort();
  }

  /**
   * Convert a param name like "degrees" to a human-readable label "Graus (°C)".
   */
  private paramNameToLabel(name: string): string {
    // Common parameter name mappings (Catalan)
    const knownLabels: Record<string, string> = {
      'degrees': 'Graus (°C)',
      'minutes': 'Temps (min)',
      'hours': 'Temps (h)',
      'seconds': 'Temps (s)',
      'temperature': 'Temperatura',
      'method': 'Mètode',
      'power': 'Potència',
      'speed': 'Velocitat',
      'amount': 'Quantitat',
      'time': 'Temps',
    };
    return knownLabels[name] || name.charAt(0).toUpperCase() + name.slice(1);
  }

  // ============================================================================
  // SHARED: SECTION HEADER, SEARCH DROPDOWN, EDIT BUTTON
  // ============================================================================

  /**
   * Render the section header row: title (left) and greyed-out ID + clear X button (right).
   */
  /**
   * Catalan label used in this card's edit/delete button tooltips — the
   * one thing that's genuinely different per entity type (grammar, not
   * layout), everything else below is shared verbatim across all three.
   */
  private entityActionLabel(entityType: EntityType): string {
    switch (entityType) {
      case 'ingredient': return 'l\'ingredient';
      case 'transformation': return 'la transformació';
      case 'tool': return 'aquest utensili';
    }
  }

  /**
   * One card for one attached ingredient/transformation/tool — the single
   * shared building block behind the Ingredient, Transformació, and
   * Utensilis tabs: a header band (edit pencil + arm/confirm delete,
   * right-aligned) above a body with the entity's image/name, then
   * whatever properties the caller renders below that (quantity for an
   * ingredient, parameters for a transformation, fresh+notes for a tool —
   * see renderIngredientNodeProperties()/renderTransformationNodeProperties()/
   * the Utensilis tab's own extra-properties callback).
   *
   * Deliberately doesn't know *where* `entity` lives (a node's single
   * ingredient/transformation slot, or one row of its tool list) — the
   * caller's `onEntityChanged`/`onRemove` callbacks own that, which is
   * exactly what lets one function serve a single slot and a list alike.
   */
  private renderEntityCard(
    container: Selection<any, any, any, any>,
    entityType: EntityType,
    entity: EntityData,
    callbacks: {
      /** Fired after a Save or a live image-pool change — write the
       *  updated entity back wherever this caller's copy of it lives. */
      onEntityChanged: (updated: EntityData) => void;
      /** Fired once the arm/confirm delete sequence completes. */
      onRemove: () => void;
      /** Render whatever node-specific fields go below the image/name —
       *  called once up front, and again after a Save (a transformation's
       *  edited render template can change which params it needs). */
      renderExtraProperties: (body: Selection<any, any, any, any>) => void;
    },
  ): void {
    const label = this.entityActionLabel(entityType);
    const card = container.append('div').attr('class', 'border border-[var(--accent-alpha-20)] rounded-md overflow-hidden');

    // `entity-card-header` is a hook for callers that need to inject
    // something on the left (e.g. renderTransformationUsageRows()'s drag
    // handle) — space-between degrades to the same look as flex-end when
    // there's nothing there, so this is a no-op for every other caller.
    const header = card.append('div').attr('class', 'entity-card-header');
    (header.node() as HTMLElement).style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 4px;
      padding: 4px 6px;
      background: var(--accent-alpha-13);
    `;
    const headerEl = header.node() as HTMLElement;

    const body = card.append('div').attr('class', 'p-2 space-y-2');

    // Grouped together so the two buttons stay adjacent at the right end
    // regardless of what else space-between puts on the header's left
    // (e.g. a drag handle) — headerEl itself never has more than two
    // children: whatever's on the left, and this group.
    const buttonsGroup = document.createElement('div');
    buttonsGroup.style.cssText = 'display: flex; align-items: center; gap: 4px; flex-shrink: 0;';
    headerEl.appendChild(buttonsGroup);

    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.title = `Edita ${label}`;
    editBtn.textContent = '✎';
    editBtn.style.cssText = `
      width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
      border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
      font-size: 13px; font-weight: bold; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.15s ease;
    `;
    buttonsGroup.appendChild(editBtn);

    const removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.title = `Elimina ${label}`;
    removeBtn.textContent = '×';
    removeBtn.style.cssText = `
      width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
      border: 1px solid var(--accent-alpha-40); background: transparent; color: var(--accent);
      font-size: 16px; font-weight: bold; cursor: pointer;
      display: flex; align-items: center; justify-content: center;
      transition: all 0.15s ease;
    `;
    buttonsGroup.appendChild(removeBtn);

    const topRow = document.createElement('div');
    topRow.style.cssText = 'display: flex; flex-direction: column; align-items: center; gap: 4px; text-align: center;';
    body.node()?.appendChild(topRow);

    let currentEntity = entity;
    const renderTopRowContent = (e: EntityData) => {
      topRow.innerHTML = '';
      const imageUrl = getDefaultImageUrl(e.images);
      if (imageUrl) {
        const img = document.createElement('img');
        img.src = imageUrl;
        img.alt = '';
        img.className = 'w-12 h-12 rounded-full object-cover';
        img.addEventListener('error', () => { img.style.display = 'none'; });
        topRow.appendChild(img);
      }
      const nameSpan = document.createElement('span');
      nameSpan.className = 'text-sm font-mono text-[#e0e0e0]';
      nameSpan.textContent = e.name;
      topRow.appendChild(nameSpan);
    };
    renderTopRowContent(currentEntity);

    const editContainer = document.createElement('div');
    body.node()?.appendChild(editContainer);

    // A dedicated, clearable container for renderExtraProperties() — it's
    // called again after a Save (template/params may have changed), and
    // without a container to clear first, that second call would just
    // append a duplicate copy alongside the first (and a third on the
    // next edit, and so on) rather than replacing it.
    const extraPropsContainer = body.append('div');
    const renderExtraProps = () => {
      extraPropsContainer.html('');
      callbacks.renderExtraProperties(extraPropsContainer);
    };

    editBtn.addEventListener('click', () => {
      headerEl.style.display = 'none';
      topRow.style.display = 'none';
      new InlineEntityEditor({
        entityType,
        container: editContainer,
        existingEntity: currentEntity,
        onUpdated: (updated) => {
          currentEntity = updated as EntityData;
          editContainer.innerHTML = '';
          headerEl.style.display = 'flex';
          topRow.style.display = 'flex';
          renderTopRowContent(currentEntity);
          callbacks.onEntityChanged(currentEntity);
          this.onEntityUpdate?.(entityType, currentEntity.id, currentEntity);
          renderExtraProps(); // template/params may have changed
        },
        onCancelled: () => {
          editContainer.innerHTML = '';
          headerEl.style.display = 'flex';
          topRow.style.display = 'flex';
          renderTopRowContent(currentEntity); // pick up any image change made before cancelling
        },
        onImagesChanged: (updated) => {
          currentEntity = updated as EntityData;
          callbacks.onEntityChanged(currentEntity);
          this.onEntityUpdate?.(entityType, currentEntity.id, currentEntity);
        },
      }).render();
    });

    // Remove — two-click arm/confirm, same pattern as List View's own row
    // delete button.
    let removeArmed = false;
    let outsideClickHandler: ((e: MouseEvent) => void) | null = null;
    const applyRemoveBtnStyle = () => {
      removeBtn.style.border = `1px solid ${removeArmed ? 'var(--accent)' : 'var(--accent-alpha-40)'}`;
      removeBtn.style.background = removeArmed ? 'var(--accent)' : 'transparent';
      removeBtn.style.color = removeArmed ? '#0d1117' : 'var(--accent)';
      removeBtn.title = removeArmed ? 'Confirma l\'eliminació' : `Elimina ${label}`;
    };
    const disarmRemove = () => {
      if (outsideClickHandler) {
        document.removeEventListener('click', outsideClickHandler);
        outsideClickHandler = null;
      }
      if (!removeArmed) return;
      removeArmed = false;
      applyRemoveBtnStyle();
    };
    removeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (removeArmed) {
        disarmRemove();
        callbacks.onRemove();
        return;
      }
      removeArmed = true;
      applyRemoveBtnStyle();
      outsideClickHandler = (ev: MouseEvent) => {
        if (ev.target !== removeBtn) disarmRemove();
      };
      document.addEventListener('click', outsideClickHandler);
    });

    renderExtraProps();
  }

  private renderSearchDropdown(
    section: Selection<any, any, any, any>,
    node: DisplayNode,
    entityType: 'ingredient'
  ): void {
    // Wrapper for click-outside detection
    const wrapper = section.append('div')
      .attr('class', 'search-dropdown-wrapper relative');

    const searchContainer = wrapper.append('div').attr('class', 'space-y-2');

    const currentEntity = node.data.ingredientInfo?.ingredient;

    const input = searchContainer.append('input')
      .attr('type', 'text')
      .attr('placeholder', 'Cerca ingredients...')
      .attr('class', 'w-full px-3 py-2 border border-[#333] bg-[#0d1117] text-[#e0e0e0] placeholder-[#666] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--accent)]')
      .attr('value', currentEntity?.name ?? '')
      .node() as HTMLInputElement;

    // Custom div-based dropdown (replaces native <select>). A normal
    // in-flow sibling of `input` — not `absolute`/`fixed` — so it never
    // overlays or gets clipped by anything: it just pushes the create
    // button (and whatever's below this popup section) down while open,
    // the same way a native <select> growing in place would. Trades a
    // layout shift on open/close for needing zero position-tracking code
    // and being immune to every overlay-positioning issue we ran into.
    const dropdownContainer = document.createElement('div');
    dropdownContainer.id = `${entityType}-dropdown-${node.id}`;
    dropdownContainer.className =
      'w-full mt-1 bg-[#1e1e2e] border border-[var(--accent-alpha-20)] rounded-md text-[#e0e0e0] max-h-[180px] overflow-y-auto shadow-lg';
    dropdownContainer.style.display = 'none';
    (searchContainer.node() as HTMLElement).appendChild(dropdownContainer);

    // `sticky bottom-0`: appended as the last child of `dropdownContainer`
    // (a scrolling container — see SearchDropdown.displayResults()), it
    // stays pinned to the bottom of that scroll area rather than scrolling
    // away with the results above it, so "Crea…" stays reachable without
    // having to scroll all the way down through a long results list first.
    const createButton = searchContainer.append('button')
      .attr('type', 'button')
      .attr('class', 'sticky bottom-0 w-full px-3 py-2 text-sm rounded-b-md border-t border-[var(--accent-alpha-27)] bg-[var(--accent)] text-[#0d1117] hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-[var(--accent)] font-mono font-bold')
      .style('display', 'none')
      .node() as HTMLButtonElement;

    // Inline editor container (empty until create is clicked). A sibling of
    // `wrapper` — NOT nested inside it — because openInlineCreateEditor()
    // hides `.search-dropdown-wrapper` while creating; nesting this one
    // inside the wrapper would hide the just-opened create form along with
    // the search box itself.
    const inlineContainer = document.createElement('div');
    inlineContainer.id = `inline-create-${entityType}-${node.id}`;
    const sectionNode = section.node();
    if (sectionNode) {
      sectionNode.appendChild(inlineContainer);
    }

    const searchDropdown = new SearchDropdown(
      entityType,
      input,
      dropdownContainer,
      createButton,
      (selected) => this.handleIngredientSelect(node, selected as Ingredient),
      (query) => {
        // Open inline create form
        this.openInlineCreateEditor(node, entityType, query);
      },
      true,
      // Outside-click boundary is the *outer* wrapper (input + create
      // button), not dropdownContainer's own tighter positioning parent —
      // clicking the create button must not count as "outside".
      wrapper.node() as HTMLElement
    );
    searchDropdown.init();
    this.searchDropdowns.push(searchDropdown);
  }

  /**
   * Suggestions-only ingredient dropdown attached to the node's own name
   * field: typing runs the same fuzzy search as the Ingredient tab's
   * search box, but picking a result just attaches that ingredient — it
   * never touches the typed name, and there's no "create new" affordance
   * here (that stays on the Ingredient tab, where a name to seed it with
   * is genuinely being chosen).
   */
  private renderNodeNameIngredientSuggestions(
    node: DisplayNode,
    nameInput: HTMLInputElement,
    /** `nameGroup` — the container `nameInput` itself lives in; see the
     *  name field's own render code, just above its call to this method. */
    wrapper: HTMLElement
  ): void {
    // A normal in-flow sibling of `nameInput`, not `absolute`/`fixed` — see
    // renderSearchDropdown()'s doc comment on the same choice.
    const dropdownContainer = document.createElement('div');
    dropdownContainer.id = `node-name-ingredient-dropdown-${node.id}`;
    dropdownContainer.className =
      'w-full mt-1 bg-[#1e1e2e] border border-[var(--accent-alpha-20)] rounded-md text-[#e0e0e0] max-h-[180px] overflow-y-auto shadow-lg';
    dropdownContainer.style.display = 'none';
    wrapper.appendChild(dropdownContainer);

    const searchDropdown = new SearchDropdown(
      'ingredient',
      nameInput,
      dropdownContainer,
      null,
      (selected) => {
        // Picking a suggestion replaces whatever was typed — the node's
        // name comes from the ingredient now (resolveNodeTitle's own
        // fallback), rather than keeping stale partial query text that
        // merely happened to match it. A name typed *without* picking a
        // suggestion is left alone — it's still a deliberate override.
        node.data.name = undefined;
        nameInput.value = '';
        // Clicking a dropdown row blurs `nameInput` first (focus moves
        // away on mousedown, before this click handler runs), which
        // already queued its own deferred commit of whatever text was
        // still in the field — see commitFieldChangeDeferred()'s doc
        // comment. Queuing this clear the same deferred way guarantees it
        // runs *after* that one, so it wins rather than being overwritten
        // by it.
        this.commitFieldChangeDeferred(node.id, 'name', undefined);
        this.handleIngredientSelect(node, selected as Ingredient);
      },
      undefined,
      false,
      wrapper
    );
    searchDropdown.init();
    this.searchDropdowns.push(searchDropdown);
  }

  /**
   * Render an "Edit ingredient" / "Edit transformation" button inside the inline container.
   * Clicking opens an inline editor in place of this button.
   */
  /**
   * Open an inline *create* form within the current section — reached from
   * the search dropdown's "Crea…" button, when no ingredient/transformation
   * is attached yet. Editing an *existing* one is handled entirely inline
   * by renderEntityCard() instead; this is create-only.
   */
  private openInlineCreateEditor(
    node: DisplayNode,
    entityType: 'ingredient',
    initialName: string,
  ): void {
    const containerId = `inline-create-${entityType}-${node.id}`;
    const container = document.getElementById(containerId);
    if (!container) return;

    // Hide the search dropdown wrapper while creating — a sibling of this
    // container (see renderSearchDropdown()'s comment), never an ancestor,
    // so this must look sideways via the shared parent rather than up via
    // closest().
    const wrapper = container.parentElement?.querySelector('.search-dropdown-wrapper') as HTMLElement | null;
    if (wrapper) {
      wrapper.style.display = 'none';
    }

    const editor = new InlineEntityEditor({
      entityType,
      container,
      initialName,
      onCreated: (created) => {
        this.closeInlineCreateEditor(node, entityType);
        this.handleIngredientSelect(node, created as Ingredient);
      },
      onCancelled: () => {
        this.closeInlineCreateEditor(node, entityType);
      },
    });
    editor.render();
  }

  private closeInlineCreateEditor(node: DisplayNode, entityType: 'ingredient'): void {
    const createContainer = document.getElementById(`inline-create-${entityType}-${node.id}`);
    if (createContainer) createContainer.innerHTML = '';

    const wrapper = createContainer?.parentElement?.querySelector('.search-dropdown-wrapper') as HTMLElement | null;
    if (wrapper) {
      wrapper.style.display = '';
    }
  }

  // ============================================================================
  // HANDLERS
  // ============================================================================

  /**
   * Remove the ingredient association from this node.
   *
   * Deliberately leaves node.data.imageUrl untouched: it's the node's own
   * explicit image reference now, independent of where it came from, so
   * detaching the ingredient shouldn't blank a working image.
   */
  private clearIngredient(node: DisplayNode): void {
    this.onFieldChange(node.id, 'ingredientInfo.ingredient', null);
    node.data.ingredientInfo = undefined;
    node.name = resolveNodeTitle(node.data);
    if (this.currentNode?.id === node.id) {
      this.renderNodeContent(node);
    }
  }

  private async handleIngredientSelect(node: DisplayNode, ingredient: Ingredient): Promise<void> {
    await this.onFieldChange(node.id, 'ingredientInfo.ingredient', ingredient);
    node.data.ingredientInfo = node.data.ingredientInfo ?? { ingredient };
    node.data.ingredientInfo.ingredient = ingredient;
    node.name = resolveNodeTitle(node.data);
    this.autoAssignImageOnAttach(node);
    if (this.currentNode?.id === node.id) {
      this.renderNodeContent(node);
    }
  }

  /**
   * When a node with no image yet gets an ingredient, transformation, or
   * tool attached, auto-assign that entity's current default image as the
   * node's own explicit image (still changeable afterwards via the image
   * pool cycler). Never overwrites an image the node already has.
   */
  private autoAssignImageOnAttach(node: DisplayNode): void {
    if (node.data.imageUrl) return;

    let toolDefaultUrl: string | undefined;
    for (const toolInfo of node.data.toolInfos ?? []) {
      toolDefaultUrl = getDefaultImageUrl(toolInfo.tool.images);
      if (toolDefaultUrl) break;
    }

    let trafoDefaultUrl: string | undefined;
    for (const trafoInfo of node.data.transformationInfos ?? []) {
      trafoDefaultUrl = getDefaultImageUrl(trafoInfo.transformation?.images);
      if (trafoDefaultUrl) break;
    }

    const defaultUrl =
      getDefaultImageUrl(node.data.ingredientInfo?.ingredient?.images)
      ?? trafoDefaultUrl
      ?? toolDefaultUrl;

    if (defaultUrl) {
      node.data.imageUrl = defaultUrl;
      this.onFieldChange(node.id, 'imageUrl', defaultUrl);
    }
  }

  // ============================================================================
  // UTILITIES
  // ============================================================================

  /**
   * Resolve the image URL for a node — its own explicit imageUrl, no more
   * derivation from an ingredient/transformation source.
   */
  private resolveNodeImageUrl(data: DisplayNode['data']): string | undefined {
    return getNodeImageUrl(data);
  }

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  /**
   * Check if a string is numeric (integer or decimal).
   */
  private isNumeric(str: string): boolean {
    if (str.length === 0) return false;
    return !isNaN(parseFloat(str)) && isFinite(Number(str));
  }

  /** Format a scaled amount, avoiding unnecessary trailing zeros. */
  private formatScaledNumber(n: number): string {
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(2).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
  }

  /**
   * Called from RecipeVisualizer when hovering/unhovering nodes during pick mode.
   * Temporarily shows the node ID in the active pick input, restoring original
   * value on unhover.
   */
  public previewPickedNodeId(nodeId: string | null): void {
    if (!this.pickInputRef) return;
    if (nodeId) {
      if (this.pickOriginalValue === undefined) {
        this.pickOriginalValue = this.pickInputRef.value;
      }
      this.pickInputRef.value = nodeId;
    } else {
      if (this.pickOriginalValue !== undefined) {
        this.pickInputRef.value = this.pickOriginalValue;
        this.pickOriginalValue = undefined;
      }
    }
  }

  /**
   * Cleanup search dropdown instances
   */
  private cleanupSearchDropdowns(): void {
    this.searchDropdowns.forEach(dropdown => dropdown.destroy());
    this.searchDropdowns = [];
  }

  /**
   * Open the unit editor dialog.
   * Dynamically imports the UnitEditor component to keep the bundle small.
   */
  private async openUnitEditor(): Promise<void> {
    try {
      const m = await import('./components/UnitEditor.js');
      const editor = new m.UnitEditor();
      await editor.open();
      // After editor closes, invalidate cache and refresh the current node
      // to show updated unit options
      unitCache.invalidate();
      await unitCache.ensureLoaded();
      if (this.currentNode) {
        this.renderNodeContent(this.currentNode);
      }
    } catch (err) {
      console.error('Failed to load UnitEditor:', err);
    }
  }

  /**
   * Cleanup method to remove the visualizer element
   */
  public destroy(): void {
    this.cleanupSearchDropdowns();
    this.visualizerElement.remove();
  }
}
